import { useEffect, useMemo, useRef, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth, useCan } from "../lib/auth";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Plus, Pencil, Trash2, LogOut, Phone, Smartphone, BadgePercent, FileText, Loader2, Upload, Settings as SettingsIcon, Users, BarChart3, Eye, Share2, FolderTree, Radio, FileSpreadsheet, Wand2, Copy, Info, Lightbulb, ThumbsUp } from "lucide-react";
import { toast } from "sonner";

export default function AdminDashboard() {
  const { user, logout, ready } = useAuth();
  const can = useCan();
  if (ready && (!user || user.role !== "admin")) return <Navigate to="/admin/login" replace />;

  // Tab registry — kept declarative so the default-tab selector and the rendered list stay in sync.
  const tabs = [
    { value: "analytics",  resource: "analytics",  Icon: BarChart3,       label: "Analytics",  testid: "tab-analytics",  Section: AnalyticsSection },
    { value: "plans",      resource: "plans",      Icon: FileText,        label: "Plans",      testid: "tab-plans",      Section: PlansSection },
    { value: "categories", resource: "categories", Icon: FolderTree,      label: "Categories", testid: "tab-categories", Section: CategoriesSection },
    { value: "devices",    resource: "devices",    Icon: Smartphone,      label: "Devices",    testid: "tab-devices",    Section: DevicesSection },
    { value: "promos",     resource: "promos",     Icon: BadgePercent,    label: "Promos",     testid: "tab-promos",     Section: PromosSection },
    { value: "carriers",   resource: "carriers",   Icon: Radio,           label: "Carriers",   testid: "tab-carriers",   Section: CarriersSection },
    { value: "addons",     resource: "addons",     Icon: Plus,            label: "Add-ons",    testid: "tab-addons",     Section: AddOnsSection },
    { value: "quotes",     resource: "quotes",     Icon: FileSpreadsheet, label: "Quotes",     testid: "tab-quotes",     Section: QuotesSection },
    { value: "suggestions", resource: "suggestions", Icon: Lightbulb,      label: "Suggestions", testid: "tab-suggestions", Section: SuggestionsSection },
    { value: "settings",   resource: "site_settings",   Icon: SettingsIcon,    label: "Settings",   testid: "tab-settings",   Section: SettingsSection, altResource: ["master_agents", "dealers", "stores"] },
    { value: "admins",     resource: "users",      Icon: Users,           label: "Users",      testid: "tab-admins",     Section: UsersAndRolesSection, altResource: ["security_roles"] },
  ];
  const visibleTabs = tabs.filter((t) => {
    // Suggestions is always visible to any authenticated admin user — every user can submit a
    // suggestion, even when their role doesn't grant `suggestions:view` for the management table.
    if (t.value === "suggestions") return true;
    if (can(t.resource, "view")) return true;
    const alts = Array.isArray(t.altResource) ? t.altResource : (t.altResource ? [t.altResource] : []);
    return alts.some((r) => can(r, "view"));
  });
  const defaultTab = visibleTabs[0]?.value || "analytics";

  return (
    <div className="min-h-screen flex" data-testid="page-admin-dashboard">
      <aside className="w-64 bg-[#0B132B] text-white p-6 flex flex-col gap-6 sticky top-0 h-screen">
        <Link to="/" className="flex items-center gap-2.5">
          <div className="h-8 w-8 bg-[#00E5FF] flex items-center justify-center">
            <Phone className="h-4 w-4 text-[#0B132B]" strokeWidth={2.5} />
          </div>
          <div>
            <div className="font-bold text-base">TW Calc</div>
            <div className="mono text-[10px] tracking-widest text-[#00E5FF]">ADMIN</div>
          </div>
        </Link>
        <div className="mono text-xs text-slate-400 uppercase tracking-wider">Signed in as</div>
        <div>
          <div className="text-sm font-semibold">{user?.name || "Admin"}</div>
          <div className="text-xs text-slate-400">{user?.email}</div>
          {user?.is_super_admin && <div className="mt-1 inline-block px-1.5 py-0.5 text-[9px] font-bold tracking-widest uppercase bg-[#00E5FF] text-[#0B132B]">Super Admin</div>}
        </div>
        <div className="mt-auto flex flex-col gap-2">
          <Link to="/" className="text-xs text-slate-300 hover:text-[#00E5FF]">← Back to site</Link>
          <button onClick={logout} className="btn-outline-navy !text-white !border-white/40 hover:!bg-white hover:!text-[#0B132B] text-sm" data-testid="admin-logout">
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>
      </aside>

      <main className="flex-1 bg-slate-50 p-10">
        <div className="tw-overline">Administration</div>
        <h1 className="mt-2 text-4xl font-extrabold tracking-tighter text-[#0B132B]">Catalog manager</h1>

        {visibleTabs.length === 0 ? (
          <div className="mt-8 bg-white border border-slate-200 p-10 text-center text-slate-500" data-testid="no-tabs-visible">
            Your role has no access to any section. Contact a super admin to assign permissions.
          </div>
        ) : (
          <Tabs defaultValue={defaultTab} className="mt-8">
            <TabsList className="bg-white border border-slate-200 rounded-none p-1 flex-wrap h-auto" data-testid="admin-tabs">
              {visibleTabs.map((t) => (
                <TabsTrigger key={t.value} value={t.value} className="rounded-none data-[state=active]:bg-[#0B132B] data-[state=active]:text-white px-5" data-testid={t.testid}>
                  <t.Icon className="h-4 w-4 mr-2" /> {t.label}
                </TabsTrigger>
              ))}
            </TabsList>
            {visibleTabs.map((t) => (
              <TabsContent key={t.value} value={t.value}><t.Section /></TabsContent>
            ))}
          </Tabs>
        )}
      </main>
    </div>
  );
}

/* ---------------- Plans ---------------- */
const PLAN_EMPTY = { name: "", data_gb: 0, unlimited: false, price_one_line: 0, multi_line_discount_pct: 0, line_tiers: [], hotspot_gb: 0, hotspot_unlimited: false, international_calling: false, features: [], category_id: "", active: true };

function PlansSection() {
  const can = useCan();
  const canManage = can("plans", "manage");
  const canDelete = can("plans", "delete");
  const [items, setItems] = useState([]); const [categories, setCategories] = useState([]); const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); const [open, setOpen] = useState(false); const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [p, c] = await Promise.all([api.get("/plans"), api.get("/categories")]);
      setItems(p.data); setCategories(c.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const catMap = useMemo(() => { const m = {}; categories.forEach((c) => (m[c.id] = c)); return m; }, [categories]);

  const onSave = async () => {
    setBusy(true);
    try {
      const payload = {
        ...editing,
        features: typeof editing.features === "string" ? editing.features.split(",").map((s) => s.trim()).filter(Boolean) : (editing.features || []),
        line_tiers: (editing.line_tiers || []).filter((t) => t && t.from_line && t.to_line),
        category_id: editing.category_id || null,
      };
      delete payload.id; delete payload.created_at;
      if (editing.id) await api.put(`/plans/${editing.id}`, payload);
      else await api.post("/plans", payload);
      toast.success("Plan saved"); setOpen(false); setEditing(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error"); } finally { setBusy(false); }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this plan?")) return;
    try { await api.delete(`/plans/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <SectionShell
      title="Plans"
      onAdd={canManage ? () => { setEditing({ ...PLAN_EMPTY }); setOpen(true); } : undefined}
      addTestId="add-plan"
      extra={canManage ? <CsvImportButton kind="plans" onDone={load} sampleHeaders="name,price_one_line,data_gb,unlimited,hotspot_gb,multi_line_discount_pct,international_calling,features,active" /> : null}
    >
      {loading ? <Loading /> : items.length === 0 ? <Empty label="No plans yet." /> : (
        <Table headers={["Name", "Category", "Data", "Price/1 line", "Hotspot", "Active", ""]}>
          {items.map((p) => (
            <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`plan-row-${p.id}`}>
              <Td><span className="font-semibold text-[#0B132B]">{p.name}</span></Td>
              <Td>{p.category_id ? <span className="badge-pill badge-outline">{catMap[p.category_id]?.name || "—"}</span> : <span className="text-slate-400 text-xs">—</span>}</Td>
              <Td className="mono">{p.unlimited ? "Unlimited" : `${p.data_gb} GB`}</Td>
              <Td className="mono">${p.price_one_line.toFixed(2)}</Td>
              <Td className="mono">{p.hotspot_unlimited ? "Unlimited" : `${p.hotspot_gb} GB`}</Td>
              <Td><Active on={p.active} /></Td>
              <Td className="text-right">
                <RowActions onEdit={() => { setEditing({ ...p, features: (p.features || []).join(", "), category_id: p.category_id || "" }); setOpen(true); }} onDelete={() => onDelete(p.id)} testKey={p.id} canEdit={canManage} canDelete={canDelete} />
              </Td>
            </tr>
          ))}
        </Table>
      )}

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditing(null); }}>
        <DialogContent className="max-w-2xl rounded-none" data-testid="plan-dialog">
          <DialogHeader><DialogTitle>{editing?.id ? "Edit plan" : "New plan"}</DialogTitle></DialogHeader>
          {editing && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Name"><input className="input-sharp" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="plan-field-name" /></Field>
              <Field label="Category">
                <select className="input-sharp" value={editing.category_id || ""} onChange={(e) => setEditing({ ...editing, category_id: e.target.value })} data-testid="plan-field-category">
                  <option value="">— None —</option>
                  {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </Field>
              <Field label="Price for 1 line ($)"><input type="number" step="0.01" className="input-sharp" value={editing.price_one_line} onChange={(e) => setEditing({ ...editing, price_one_line: parseFloat(e.target.value || 0) })} data-testid="plan-field-price" /></Field>
              <Field label="Data (GB)"><input type="number" className="input-sharp" value={editing.data_gb} onChange={(e) => setEditing({ ...editing, data_gb: parseFloat(e.target.value || 0) })} disabled={editing.unlimited} /></Field>
              <Field label="Hotspot (GB)"><input type="number" className="input-sharp" value={editing.hotspot_gb} onChange={(e) => setEditing({ ...editing, hotspot_gb: parseFloat(e.target.value || 0) })} disabled={editing.hotspot_unlimited} /></Field>
              <Field label="Features (comma-separated)" full><input className="input-sharp" value={editing.features} onChange={(e) => setEditing({ ...editing, features: e.target.value })} /></Field>
              <Toggle label="Unlimited data" v={editing.unlimited} onChange={(v) => setEditing({ ...editing, unlimited: v, data_gb: v ? 9999 : editing.data_gb })} />
              <Toggle label="Unlimited hotspot" v={editing.hotspot_unlimited} onChange={(v) => setEditing({ ...editing, hotspot_unlimited: v, hotspot_gb: v ? 9999 : editing.hotspot_gb })} />
              <Toggle label="International calling" v={editing.international_calling} onChange={(v) => setEditing({ ...editing, international_calling: v })} />
              <Toggle label="Active" v={editing.active} onChange={(v) => setEditing({ ...editing, active: v })} />
              <PlanTiersEditor
                tiers={editing.line_tiers || []}
                onChange={(line_tiers) => setEditing({ ...editing, line_tiers })}
              />
            </div>
          )}
          <DialogFooter>
            <button onClick={() => setOpen(false)} className="btn-outline-navy text-sm !py-2.5" data-testid="plan-cancel">Cancel</button>
            <button onClick={onSave} disabled={busy} className="btn-brand text-sm !py-2.5" data-testid="plan-save">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Save</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </SectionShell>
  );
}

/* ---------------- Devices ---------------- */
const EMPTY_SUBVARIANT = {
  enabled: false,
  device_price: 0,
  verify_id: "not_required",
  port_in: "not_required",
  eligible_carrier_ids: [],
  eligible_plan_ids: [],
  required_months: 0,
  required_devices: 0,
};
const EMPTY_GROUP = {
  single_month: { ...EMPTY_SUBVARIANT },
  multi_month: { ...EMPTY_SUBVARIANT },
  multi_line: { ...EMPTY_SUBVARIANT },
};
const EMPTY_FINANCE = { enabled: false, device_price_tier1: 0, device_price_tier2: 0, device_price_tier3: 0, months: 24, fcot_credit: 0, eligible_plan_ids: [] };
const EMPTY_FULL_RETAIL = { enabled: false, price: 0, verify_id: "not_required", port_in: "not_required", eligible_carrier_ids: [], eligible_plan_ids: [] };
const EMPTY_PHONE_PRICING = {
  finance: { ...EMPTY_FINANCE },
  switch: JSON.parse(JSON.stringify(EMPTY_GROUP)),
  join: JSON.parse(JSON.stringify(EMPTY_GROUP)),
  no_hassle: JSON.parse(JSON.stringify(EMPTY_GROUP)),
  full_retail: { ...EMPTY_FULL_RETAIL },
};

const EMPTY_TABLET_PRICING = {
  standalone: { enabled: false, price: 0, eligible_plan_ids: [] },
  bundle: { enabled: false, price: 0, eligible_plan_ids: [], required_months: 0 },
};

const EMPTY_HI_PRICING = {
  standalone: { enabled: false, price: 0 },
  bundle: { enabled: false, price: 0 },
};
const DEVICE_EMPTY = { brand: "", model: "", image_url: "", msrp: 0, storage: "", color: "", description: "", category_id: "", phone_pricing: null, active: true };

function DevicesSection() {
  const can = useCan();
  const canManage = can("devices", "manage");
  const canDelete = can("devices", "delete");
  const [items, setItems] = useState([]); const [categories, setCategories] = useState([]); const [carriers, setCarriers] = useState([]); const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); const [open, setOpen] = useState(false); const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [d, c, cr] = await Promise.all([api.get("/devices"), api.get("/categories"), api.get("/carriers", { params: { active_only: true } })]);
      setItems(d.data); setCategories(c.data); setCarriers(cr.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const catMap = useMemo(() => { const m = {}; categories.forEach((c) => (m[c.id] = c)); return m; }, [categories]);
  const isPhoneCategory = editing && editing.category_id && (catMap[editing.category_id]?.name || "").toLowerCase() === "phones";

  // Column filters (one per filterable header). `active` accepts "active"/"inactive"/"" for "any".
  const [filters, setFilters] = useState({ brand: "", model: "", category: "", msrp: "", storage: "", active: "" });
  const setFilter = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

  const filteredItems = useMemo(() => {
    const f = filters;
    const norm = (s) => String(s ?? "").toLowerCase();
    const list = items.filter((d) => {
      if (f.brand && !norm(d.brand).includes(norm(f.brand))) return false;
      if (f.model && !norm(d.model).includes(norm(f.model))) return false;
      if (f.category) {
        const catName = norm(catMap[d.category_id]?.name);
        if (!catName.includes(norm(f.category))) return false;
      }
      if (f.msrp && !String(d.msrp).includes(f.msrp)) return false;
      if (f.storage && !norm(d.storage).includes(norm(f.storage))) return false;
      if (f.active === "active" && !d.active) return false;
      if (f.active === "inactive" && d.active) return false;
      return true;
    });
    // Alphabetical default — Brand then Model.
    list.sort((a, b) => {
      const ab = norm(a.brand), bb = norm(b.brand);
      if (ab !== bb) return ab < bb ? -1 : 1;
      const am = norm(a.model), bm = norm(b.model);
      return am < bm ? -1 : am > bm ? 1 : 0;
    });
    return list;
  }, [items, filters, catMap]);

  const onSave = async () => {
    setBusy(true);
    try {
      const p = { ...editing };
      if (!p.category_id) p.category_id = null;
      // Only send phone_pricing when category is Phones and at least one scenario enabled
      if (!isPhoneCategory) {
        p.phone_pricing = null;
      } else if (p.phone_pricing) {
        const pp = p.phone_pricing;
        const anyEnabled = pp.finance?.enabled || pp.switch?.enabled || pp.join?.enabled || pp.no_hassle?.enabled || pp.full_retail?.enabled;
        if (!anyEnabled) p.phone_pricing = null;
      }
      delete p.id; delete p.created_at;
      if (editing.id) await api.put(`/devices/${editing.id}`, p);
      else await api.post("/devices", p);
      toast.success("Device saved"); setOpen(false); setEditing(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const onDelete = async (id) => {
    if (!window.confirm("Delete this device?")) return;
    try { await api.delete(`/devices/${id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const openEdit = (d) => {
    setEditing({
      ...d,
      category_id: d.category_id || "",
      phone_pricing: d.phone_pricing ? mergePhonePricing(d.phone_pricing) : { ...EMPTY_PHONE_PRICING },
    });
    setOpen(true);
  };
  const openNew = () => {
    setEditing({ ...DEVICE_EMPTY, phone_pricing: { ...EMPTY_PHONE_PRICING } });
    setOpen(true);
  };

  return (
    <SectionShell title="Devices" onAdd={canManage ? openNew : undefined} addTestId="add-device" extra={canManage ? (
      <div className="flex gap-2">
        <CsvSampleDownloadButton
          filename="devices_sample.csv"
          headers="brand,model,msrp,storage,color,image_url,description,category_id,active"
          sampleRow={'Apple,iPhone 15,729.99,128GB,Black,https://example.com/iphone15.jpg,"Apple iPhone 15 6.1in",,true'}
        />
        <CsvImportButton kind="devices" onDone={load} sampleHeaders="brand,model,msrp,storage,color,image_url,description,category_id,active" />
      </div>
    ) : null}>
      {loading ? <Loading /> : items.length === 0 ? <Empty label="No devices yet." /> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="devices-table">
            <thead>
              <tr className="text-left border-b border-slate-200">
                {["Brand", "Model", "Category", "MSRP", "Storage", "Active", ""].map((h, i) => (
                  <th key={i} className="tw-overline py-3 px-4">{h}</th>
                ))}
              </tr>
              <tr className="border-b border-slate-200 bg-slate-50">
                <th className="px-4 py-2"><input className="input-sharp !py-1.5 text-xs w-full" placeholder="Filter brand…" value={filters.brand} onChange={(e) => setFilter("brand", e.target.value)} data-testid="device-filter-brand" /></th>
                <th className="px-4 py-2"><input className="input-sharp !py-1.5 text-xs w-full" placeholder="Filter model…" value={filters.model} onChange={(e) => setFilter("model", e.target.value)} data-testid="device-filter-model" /></th>
                <th className="px-4 py-2"><input className="input-sharp !py-1.5 text-xs w-full" placeholder="Filter category…" value={filters.category} onChange={(e) => setFilter("category", e.target.value)} data-testid="device-filter-category" /></th>
                <th className="px-4 py-2"><input className="input-sharp !py-1.5 text-xs w-full" placeholder="MSRP…" value={filters.msrp} onChange={(e) => setFilter("msrp", e.target.value)} data-testid="device-filter-msrp" /></th>
                <th className="px-4 py-2"><input className="input-sharp !py-1.5 text-xs w-full" placeholder="Filter storage…" value={filters.storage} onChange={(e) => setFilter("storage", e.target.value)} data-testid="device-filter-storage" /></th>
                <th className="px-4 py-2">
                  <select className="input-sharp !py-1.5 text-xs w-full" value={filters.active} onChange={(e) => setFilter("active", e.target.value)} data-testid="device-filter-active">
                    <option value="">All</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </th>
                <th className="px-4 py-2 text-right">
                  {(filters.brand || filters.model || filters.category || filters.msrp || filters.storage || filters.active) && (
                    <button
                      type="button"
                      onClick={() => setFilters({ brand: "", model: "", category: "", msrp: "", storage: "", active: "" })}
                      className="text-[10px] mono uppercase tracking-widest text-[#008B99] hover:text-[#0B132B] underline"
                      data-testid="device-filter-clear"
                    >Clear</button>
                  )}
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredItems.length === 0 ? (
                <tr><td colSpan={7} className="py-10 text-center text-slate-500" data-testid="device-table-empty">No devices match the current filters.</td></tr>
              ) : filteredItems.map((d) => (
                <tr key={d.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`device-row-${d.id}`}>
                  <Td>{d.brand}</Td>
                  <Td><span className="font-semibold text-[#0B132B]">{d.model}</span></Td>
                  <Td>{d.category_id ? <span className="badge-pill badge-outline">{catMap[d.category_id]?.name || "—"}</span> : <span className="text-slate-400 text-xs">—</span>}</Td>
                  <Td className="mono">${d.msrp.toFixed(2)}</Td>
                  <Td className="mono">{d.storage}</Td>
                  <Td><Active on={d.active} /></Td>
                  <Td className="text-right">
                    <RowActions onEdit={() => openEdit(d)} onDelete={() => onDelete(d.id)} testKey={d.id} canEdit={canManage} canDelete={canDelete} />
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditing(null); }}>
        <DialogContent className="max-w-3xl rounded-none max-h-[90vh] overflow-y-auto" data-testid="device-dialog">
          <DialogHeader><DialogTitle>{editing?.id ? "Edit device" : "New device"}</DialogTitle></DialogHeader>
          {editing && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Brand"><input className="input-sharp" value={editing.brand} onChange={(e) => setEditing({ ...editing, brand: e.target.value })} data-testid="device-field-brand" /></Field>
              <Field label="Model"><input className="input-sharp" value={editing.model} onChange={(e) => setEditing({ ...editing, model: e.target.value })} data-testid="device-field-model" /></Field>
              <Field label="Category" full>
                <select className="input-sharp" value={editing.category_id || ""} onChange={(e) => setEditing({ ...editing, category_id: e.target.value })} data-testid="device-field-category">
                  <option value="">— None —</option>
                  {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </Field>
              <Field label="MSRP ($)"><input type="number" step="0.01" className="input-sharp" value={editing.msrp} onChange={(e) => setEditing({ ...editing, msrp: parseFloat(e.target.value || 0) })} data-testid="device-field-msrp" /></Field>
              <Field label="Storage"><input className="input-sharp" value={editing.storage} onChange={(e) => setEditing({ ...editing, storage: e.target.value })} placeholder="128GB" /></Field>
              <Field label="Color"><input className="input-sharp" value={editing.color} onChange={(e) => setEditing({ ...editing, color: e.target.value })} /></Field>
              <Toggle label="Active" v={editing.active} onChange={(v) => setEditing({ ...editing, active: v })} />
              <Field label="Image URL" full><input className="input-sharp" value={editing.image_url} onChange={(e) => setEditing({ ...editing, image_url: e.target.value })} placeholder="https://…" /></Field>
              <Field label="Description" full><textarea className="input-sharp h-24" value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} /></Field>
            </div>
          )}
          <DialogFooter>
            <button onClick={() => setOpen(false)} className="btn-outline-navy text-sm !py-2.5">Cancel</button>
            <button onClick={onSave} disabled={busy} className="btn-brand text-sm !py-2.5" data-testid="device-save">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Save</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </SectionShell>
  );
}

function mergeSubVariant(v) { return { ...EMPTY_SUBVARIANT, ...(v || {}) }; }
function mergeGroup(g) {
  return {
    single_month: mergeSubVariant(g?.single_month),
    multi_month: mergeSubVariant(g?.multi_month),
    multi_line: mergeSubVariant(g?.multi_line),
  };
}
function mergePhonePricing(pp) {
  return {
    finance: { ...EMPTY_FINANCE, ...(pp?.finance || {}) },
    switch: mergeGroup(pp?.switch),
    join: mergeGroup(pp?.join),
    no_hassle: mergeGroup(pp?.no_hassle),
    full_retail: { ...EMPTY_FULL_RETAIL, ...(pp?.full_retail || {}) },
  };
}

/**
 * PhonePricingEditor — new shape with sub-variants per scenario.
 * Props:
 *   value: PhonePricing
 *   onChange: (next) => void
 *   carriers: [{id,name}]
 *   eligiblePlans: [{id,name}] — already filtered to the device's category
 */
function PhonePricingEditor({ value, onChange, carriers, eligiblePlans }) {
  const setScenario = (key, patch) => onChange({ ...value, [key]: { ...value[key], ...patch } });
  const setSubVariant = (group, sub, patch) => onChange({
    ...value,
    [group]: { ...value[group], [sub]: { ...value[group][sub], ...patch } },
  });

  return (
    <div className="sm:col-span-2 border border-[#0B132B] p-4 bg-slate-50" data-testid="phone-pricing-editor">
      <div className="flex items-center justify-between mb-1">
        <div className="tw-overline">Phone pricing scenarios</div>
        <span className="badge-pill badge-navy">Phones only</span>
      </div>
      <p className="text-xs text-slate-500 mb-4">Enable any acquisition paths offered for this device. Customers may qualify for one or more.</p>

      <FinanceCard
        value={value.finance}
        eligiblePlans={eligiblePlans}
        onChange={(patch) => setScenario("finance", patch)}
      />

      <GroupCard
        label="Switch"
        testKey="switch"
        value={value.switch}
        carriers={carriers}
        eligiblePlans={eligiblePlans}
        onSubVariant={(sub, patch) => setSubVariant("switch", sub, patch)}
      />
      <GroupCard
        label="Join"
        testKey="join"
        value={value.join}
        carriers={carriers}
        eligiblePlans={eligiblePlans}
        onSubVariant={(sub, patch) => setSubVariant("join", sub, patch)}
        note="Port-in optional = customer may take a new number OR port from an approved carrier."
      />
      <GroupCard
        label="No Hassle"
        testKey="no_hassle"
        value={value.no_hassle}
        carriers={carriers}
        eligiblePlans={eligiblePlans}
        onSubVariant={(sub, patch) => setSubVariant("no_hassle", sub, patch)}
      />

      <FullRetailCard
        value={value.full_retail}
        carriers={carriers}
        eligiblePlans={eligiblePlans}
        onChange={(patch) => setScenario("full_retail", patch)}
      />
    </div>
  );
}

function FinanceCard({ value, eligiblePlans, onChange }) {
  const months = Math.max(1, parseInt(value.months || 24, 10));
  const monthlyFor = (price) => Math.max(0, ((price || 0) - (value.fcot_credit || 0)) / months);
  const togglePlan = (pid) => {
    const cur = value.eligible_plan_ids || [];
    onChange({ eligible_plan_ids: cur.includes(pid) ? cur.filter((x) => x !== pid) : [...cur, pid] });
  };
  return (
    <div className="bg-white border border-slate-200 mb-3" data-testid="scenario-finance">
      <ScenarioHeader title="Finance" enabled={value.enabled} onToggle={(v) => onChange({ enabled: v })} accent="amber" />
      {value.enabled && (
        <div className="p-4 border-t border-slate-200 space-y-4">
          <p className="text-[11px] text-slate-500 italic">3 device-price tiers (based on customer credit). Monthly = (tier − FCOT) ÷ months.</p>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {[1, 2, 3].map((n) => {
              const k = `device_price_tier${n}`;
              return (
                <Field key={n} label={`Device price — Tier ${n} ($)`}>
                  <input
                    type="number" step="0.01" className="input-sharp" value={value[k]}
                    onChange={(e) => onChange({ [k]: parseFloat(e.target.value || 0) })}
                    data-testid={`finance-tier${n}`}
                  />
                  <div className="mono text-[11px] text-slate-500 mt-1 ticker-num" data-testid={`finance-tier${n}-monthly`}>
                    = ${monthlyFor(value[k]).toFixed(2)}/mo
                  </div>
                </Field>
              );
            })}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Months of finance">
              <input type="number" min="1" className="input-sharp" value={value.months}
                onChange={(e) => onChange({ months: parseInt(e.target.value || 24, 10) })}
                data-testid="finance-months" />
            </Field>
            <Field label="FCOT credit ($)">
              <input type="number" step="0.01" className="input-sharp" value={value.fcot_credit}
                onChange={(e) => onChange({ fcot_credit: parseFloat(e.target.value || 0) })}
                data-testid="finance-fcot" />
            </Field>
          </div>

          <PlanPicker eligiblePlans={eligiblePlans} selected={value.eligible_plan_ids} onToggle={togglePlan} testKey="finance" />

          <p className="text-[11px] text-slate-500">Tier 1 = best credit, Tier 3 = standard. Customers see all three side-by-side and the system uses the lowest tier they qualify for.</p>
        </div>
      )}
    </div>
  );
}

/** Group of 3 sub-variants for Switch / Join / No Hassle. */
function GroupCard({ label, testKey, value, carriers, eligiblePlans, onSubVariant, note }) {
  const anyEnabled = value.single_month?.enabled || value.multi_month?.enabled || value.multi_line?.enabled;
  return (
    <div className="bg-white border border-slate-200 mb-3" data-testid={`scenario-${testKey}`}>
      <div className="w-full flex items-center justify-between px-4 py-3 border-b border-slate-100">
        <div className="flex items-center gap-3">
          <span className={`h-2.5 w-2.5 ${anyEnabled ? "bg-[#00E5FF]" : "bg-slate-300"}`} />
          <span className="font-bold text-[#0B132B]">{label}</span>
        </div>
        <span className={`text-xs mono uppercase tracking-widest ${anyEnabled ? "text-[#008B99]" : "text-slate-400"}`}>
          {anyEnabled ? `${[value.single_month?.enabled, value.multi_month?.enabled, value.multi_line?.enabled].filter(Boolean).length} variant(s)` : "Off"}
        </span>
      </div>
      <div className="p-3 space-y-3">
        <SubVariantCard
          parentKey={testKey}
          subKey="single_month"
          label="Single month"
          value={value.single_month}
          carriers={carriers}
          eligiblePlans={eligiblePlans}
          onChange={(patch) => onSubVariant("single_month", patch)}
        />
        <SubVariantCard
          parentKey={testKey}
          subKey="multi_month"
          label="Multi month"
          showRequiredMonths
          value={value.multi_month}
          carriers={carriers}
          eligiblePlans={eligiblePlans}
          onChange={(patch) => onSubVariant("multi_month", patch)}
        />
        <SubVariantCard
          parentKey={testKey}
          subKey="multi_line"
          label="Multi line"
          showRequiredDevices
          value={value.multi_line}
          carriers={carriers}
          eligiblePlans={eligiblePlans}
          onChange={(patch) => onSubVariant("multi_line", patch)}
        />
        {note && <p className="text-[11px] text-slate-500 italic px-1">{note}</p>}
      </div>
    </div>
  );
}

function SubVariantCard({ parentKey, subKey, label, value, eligiblePlans, onChange, showRequiredMonths, showRequiredDevices }) {
  const testKey = `${parentKey}-${subKey}`;
  const togglePlan = (pid) => {
    const cur = value.eligible_plan_ids || [];
    onChange({ eligible_plan_ids: cur.includes(pid) ? cur.filter((x) => x !== pid) : [...cur, pid] });
  };
  return (
    <div className="bg-slate-50 border border-slate-200" data-testid={`subvariant-${testKey}`}>
      <ScenarioHeader title={label} enabled={value.enabled} onToggle={(v) => onChange({ enabled: v })} />
      {value.enabled && (
        <div className="p-4 grid grid-cols-1 sm:grid-cols-3 gap-3 border-t border-slate-200 bg-white">
          <Field label="Device price ($)">
            <input type="number" step="0.01" className="input-sharp" value={value.device_price}
              onChange={(e) => onChange({ device_price: parseFloat(e.target.value || 0) })}
              data-testid={`subvariant-${testKey}-price`} />
          </Field>
          {showRequiredMonths && (
            <Field label="Required # of months of service">
              <input type="number" min="0" className="input-sharp" value={value.required_months}
                onChange={(e) => onChange({ required_months: parseInt(e.target.value || 0, 10) })}
                data-testid={`subvariant-${testKey}-required-months`} />
            </Field>
          )}
          {showRequiredDevices && (
            <Field label="Required # of devices">
              <input type="number" min="0" className="input-sharp" value={value.required_devices}
                onChange={(e) => onChange({ required_devices: parseInt(e.target.value || 0, 10) })}
                data-testid={`subvariant-${testKey}-required-devices`} />
            </Field>
          )}
          <Field label="Verify ID">
            <TriToggle value={value.verify_id} onChange={(v) => onChange({ verify_id: v })} testKey={`${testKey}-verify-id`} />
          </Field>
          <Field label="Port-in">
            <TriToggle value={value.port_in} onChange={(v) => onChange({ port_in: v })} testKey={`${testKey}-port-in`} />
          </Field>
          {value.port_in !== "not_required" && (
            <p className="sm:col-span-3 text-[11px] text-slate-500 italic">
              Eligible carriers are configured on the Carriers tab via "Eligible for port-in promo".
            </p>
          )}
          <PlanPicker eligiblePlans={eligiblePlans} selected={value.eligible_plan_ids} onToggle={togglePlan} testKey={testKey} />
        </div>
      )}
    </div>
  );
}

function FullRetailCard({ value, eligiblePlans, onChange }) {
  const togglePlan = (pid) => {
    const cur = value.eligible_plan_ids || [];
    onChange({ eligible_plan_ids: cur.includes(pid) ? cur.filter((x) => x !== pid) : [...cur, pid] });
  };
  return (
    <div className="bg-white border border-slate-200 mb-3" data-testid="scenario-full_retail">
      <ScenarioHeader title="Full Retail" enabled={value.enabled} onToggle={(v) => onChange({ enabled: v })} />
      {value.enabled && (
        <div className="p-4 grid grid-cols-1 sm:grid-cols-3 gap-3 border-t border-slate-200">
          <Field label="Device price ($)">
            <input type="number" step="0.01" className="input-sharp" value={value.price}
              onChange={(e) => onChange({ price: parseFloat(e.target.value || 0) })}
              data-testid="scenario-full_retail-price" />
          </Field>
          <Field label="Verify ID">
            <TriToggle value={value.verify_id} onChange={(v) => onChange({ verify_id: v })} testKey="full_retail-verify-id" />
          </Field>
          <Field label="Port-in">
            <TriToggle value={value.port_in} onChange={(v) => onChange({ port_in: v })} testKey="full_retail-port-in" />
          </Field>
          {value.port_in !== "not_required" && (
            <p className="sm:col-span-3 text-[11px] text-slate-500 italic">
              Eligible carriers are configured on the Carriers tab via "Eligible for port-in promo".
            </p>
          )}
          <PlanPicker eligiblePlans={eligiblePlans} selected={value.eligible_plan_ids} onToggle={togglePlan} testKey="full_retail" />
          <p className="sm:col-span-3 text-[11px] text-slate-500 italic">Fallback when customer doesn't qualify for other paths.</p>
        </div>
      )}
    </div>
  );
}

function CarrierPicker({ carriers, selected, onToggle, testKey }) {
  return (
    <div className="sm:col-span-3" data-testid={`carrier-picker-${testKey}`}>
      <div className="tw-overline mb-2">Eligible port-in carriers</div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {carriers.map((c) => {
          const sel = (selected || []).includes(c.id);
          return (
            <button
              key={c.id} type="button" onClick={() => onToggle(c.id)}
              className={`text-left px-3 py-2 border-2 text-sm transition-all ${sel ? "border-[#00E5FF] bg-[#00E5FF]/10" : "border-slate-200 hover:border-slate-400"}`}
              data-testid={`carrier-pick-${testKey}-${c.id}`}
            >
              <span className="flex items-center justify-between gap-2">
                <span className="font-medium text-[#0B132B]">{c.name}</span>
                <span className={`h-4 w-4 grid place-items-center border-2 ${sel ? "border-[#00E5FF] bg-[#00E5FF]" : "border-slate-300"}`}>
                  {sel && <span className="text-[10px] text-[#0B132B]">✓</span>}
                </span>
              </span>
            </button>
          );
        })}
        {carriers.length === 0 && <p className="text-xs text-slate-500 col-span-full">No active carriers. Add them under Carriers tab.</p>}
      </div>
    </div>
  );
}

function PlanPicker({ eligiblePlans, selected, onToggle, testKey }) {
  return (
    <div className="sm:col-span-3" data-testid={`plan-picker-${testKey}`}>
      <div className="tw-overline mb-2">Qualifying plans</div>
      <p className="text-[11px] text-slate-500 mb-2">Only plans in the same category as this device are shown. Leave all unchecked to accept any in-category plan.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {eligiblePlans.length === 0 ? (
          <p className="text-xs text-slate-500 col-span-full">No plans share this device's category. Add a plan with the same category to qualify here.</p>
        ) : (
          eligiblePlans.map((p) => {
            const sel = (selected || []).includes(p.id);
            return (
              <button
                key={p.id} type="button" onClick={() => onToggle(p.id)}
                className={`text-left px-3 py-2 border-2 text-sm transition-all ${sel ? "border-[#00E5FF] bg-[#00E5FF]/10" : "border-slate-200 hover:border-slate-400"}`}
                data-testid={`plan-pick-${testKey}-${p.id}`}
              >
                <span className="flex items-center justify-between gap-2">
                  <span className="font-medium text-[#0B132B] truncate">{p.name}</span>
                  <span className={`h-4 w-4 grid place-items-center border-2 ${sel ? "border-[#00E5FF] bg-[#00E5FF]" : "border-slate-300"}`}>
                    {sel && <span className="text-[10px] text-[#0B132B]">✓</span>}
                  </span>
                </span>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}

function ScenarioHeader({ title, enabled, onToggle, accent }) {
  return (
    <button type="button" onClick={() => onToggle(!enabled)} className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-50 transition" data-testid={`scenario-toggle-${title.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center gap-3">
        <span className={`h-6 w-11 ${enabled ? (accent === "amber" ? "bg-[#FFB703]" : "bg-[#00E5FF]") : "bg-slate-300"} relative transition`}>
          <span className={`absolute top-0.5 ${enabled ? "left-6" : "left-0.5"} h-5 w-5 bg-[#0B132B] transition-all`} />
        </span>
        <span className="font-bold text-[#0B132B]">{title}</span>
      </div>
      <span className={`text-xs mono uppercase tracking-widest ${enabled ? "text-[#008B99]" : "text-slate-400"}`}>{enabled ? "Enabled" : "Off"}</span>
    </button>
  );
}

function TriToggle({ value, onChange, testKey }) {
  const opts = [
    { v: "not_required", label: "Not req." },
    { v: "optional", label: "Optional" },
    { v: "required", label: "Required" },
  ];
  return (
    <div className="flex border-2 border-slate-200" role="radiogroup" data-testid={`tri-${testKey}`}>
      {opts.map((o, i) => (
        <button
          key={o.v}
          type="button"
          role="radio"
          aria-checked={value === o.v}
          onClick={() => onChange(o.v)}
          className={`flex-1 text-xs font-semibold py-2 px-2 transition ${i > 0 ? "border-l-2 border-slate-200" : ""} ${
            value === o.v ? "bg-[#0B132B] text-white" : "bg-white text-slate-600 hover:text-[#0B132B]"
          }`}
          data-testid={`tri-${testKey}-${o.v}`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/* ---------- Tablet pricing editor ---------- */
function mergeTabletPricing(tp) {
  return {
    standalone: { enabled: false, price: 0, eligible_plan_ids: [], ...(tp?.standalone || {}) },
    bundle: { enabled: false, price: 0, eligible_plan_ids: [], required_months: 0, ...(tp?.bundle || {}) },
  };
}

function TabletPricingEditor({ value, onChange, eligiblePlans }) {
  const setStandalone = (patch) => onChange({ ...value, standalone: { ...value.standalone, ...patch } });
  const setBundle = (patch) => onChange({ ...value, bundle: { ...value.bundle, ...patch } });
  const toggleBundlePlan = (pid) => {
    const cur = value.bundle.eligible_plan_ids || [];
    setBundle({ eligible_plan_ids: cur.includes(pid) ? cur.filter((x) => x !== pid) : [...cur, pid] });
  };
  return (
    <div className="sm:col-span-2 border border-[#0B132B] p-4 bg-slate-50" data-testid="tablet-pricing-editor">
      <div className="flex items-center justify-between mb-1">
        <div className="tw-overline">Tablet pricing scenarios</div>
        <span className="badge-pill badge-navy">Tablets only</span>
      </div>
      <p className="text-xs text-slate-500 mb-4">Standalone and Bundle device prices. Bundle = quote must have at least one phone line; max 2 promo tablets per account, beyond which pricing reverts to standalone.</p>

      {/* Standalone */}
      <div className="bg-white border border-slate-200 mb-3" data-testid="tablet-standalone">
        <ScenarioHeader title="Standalone" enabled={value.standalone.enabled} onToggle={(v) => setStandalone({ enabled: v })} />
        {value.standalone.enabled && (
          <div className="p-4 border-t border-slate-200 space-y-3">
            <Field label="Device price ($)">
              <input type="number" step="0.01" className="input-sharp max-w-xs" value={value.standalone.price}
                onChange={(e) => setStandalone({ price: parseFloat(e.target.value || 0) })}
                data-testid="tablet-standalone-price" />
            </Field>
            <p className="text-[11px] text-slate-500 italic">Standalone tablets aren't restricted to a specific plan list.</p>
          </div>
        )}
      </div>

      {/* Bundle */}
      <div className="bg-white border border-slate-200 mb-3" data-testid="tablet-bundle">
        <ScenarioHeader title="Smartphone bundle" enabled={value.bundle.enabled} onToggle={(v) => setBundle({ enabled: v })} accent="amber" />
        {value.bundle.enabled && (
          <div className="p-4 border-t border-slate-200 space-y-3">
            <p className="text-[11px] text-slate-500 italic">Requires at least one phone line on the same quote. Capped at 2 promo tablets per account.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Device price ($)">
                <input type="number" step="0.01" className="input-sharp" value={value.bundle.price}
                  onChange={(e) => setBundle({ price: parseFloat(e.target.value || 0) })}
                  data-testid="tablet-bundle-price" />
              </Field>
              <Field label="Required # of months of service">
                <input type="number" min="0" className="input-sharp" value={value.bundle.required_months}
                  onChange={(e) => setBundle({ required_months: parseInt(e.target.value || 0, 10) })}
                  data-testid="tablet-bundle-months" />
              </Field>
            </div>
            <PlanPicker
              eligiblePlans={eligiblePlans}
              selected={value.bundle.eligible_plan_ids}
              onToggle={toggleBundlePlan}
              testKey="tablet-bundle"
            />
          </div>
        )}
      </div>
    </div>
  );
}

/* ---------- Home Internet pricing editor ---------- */
function mergeHomeInternetPricing(hp) {
  return {
    standalone: { enabled: false, price: 0, ...(hp?.standalone || {}) },
    bundle: { enabled: false, price: 0, ...(hp?.bundle || {}) },
  };
}

function HotspotPricingEditor({ value, onChange, eligiblePlans }) {
  const togglePlan = (pid) => {
    const cur = value?.eligible_plan_ids || [];
    onChange({ ...value, eligible_plan_ids: cur.includes(pid) ? cur.filter((x) => x !== pid) : [...cur, pid] });
  };
  return (
    <div className="sm:col-span-2 border border-[#0B132B] p-4 bg-slate-50" data-testid="hotspot-pricing-editor">
      <div className="flex items-center justify-between mb-1">
        <div className="tw-overline">Hotspot promo price</div>
        <span className="badge-pill badge-navy">Hotspot only</span>
      </div>
      <p className="text-xs text-slate-500 mb-4">Flat one-time promo price for this hotspot device. No standalone/bundle split.</p>
      <div className="bg-white border border-slate-200 p-4" data-testid="hotspot-pricing-row">
        <Toggle
          label="Enable promo pricing"
          v={!!value?.enabled}
          onChange={(v) => onChange({ ...(value || {}), enabled: v, price: value?.price || 0, eligible_plan_ids: value?.eligible_plan_ids || [] })}
        />
        {value?.enabled && (
          <div className="mt-3 space-y-4">
            <Field label="Promo price ($)">
              <input
                type="number" step="0.01" min="0"
                className="input-sharp max-w-xs"
                value={value?.price ?? 0}
                onChange={(e) => onChange({ ...value, price: parseFloat(e.target.value || 0) })}
                data-testid="hotspot-promo-price"
              />
            </Field>
            <div>
              <div className="tw-overline mb-2">Eligible data plans</div>
              <p className="text-[11px] text-slate-500 mb-2 italic">Plans shown on the device detail page in "$X NAME / $Y NAME" format.</p>
              <PlanPicker
                eligiblePlans={eligiblePlans}
                selected={value?.eligible_plan_ids || []}
                onToggle={togglePlan}
                testKey="hotspot-plans"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


function HomeInternetPricingEditor({ value, onChange }) {
  const setStandalone = (patch) => onChange({ ...value, standalone: { ...value.standalone, ...patch } });
  const setBundle = (patch) => onChange({ ...value, bundle: { ...value.bundle, ...patch } });
  return (
    <div className="sm:col-span-2 border border-[#0B132B] p-4 bg-slate-50" data-testid="home-internet-pricing-editor">
      <div className="flex items-center justify-between mb-1">
        <div className="tw-overline">Home Internet pricing scenarios</div>
        <span className="badge-pill badge-navy">Home Internet only</span>
      </div>
      <p className="text-xs text-slate-500 mb-4">Standalone and Bundle device prices. Bundle = quote must have at least one phone line; max 1 promo home internet per account, beyond which pricing reverts to standalone.</p>

      {/* Standalone */}
      <div className="bg-white border border-slate-200 mb-3" data-testid="hi-standalone">
        <ScenarioHeader title="Standalone" enabled={value.standalone.enabled} onToggle={(v) => setStandalone({ enabled: v })} />
        {value.standalone.enabled && (
          <div className="p-4 border-t border-slate-200 space-y-3">
            <Field label="Device price ($)">
              <input type="number" step="0.01" className="input-sharp max-w-xs" value={value.standalone.price}
                onChange={(e) => setStandalone({ price: parseFloat(e.target.value || 0) })}
                data-testid="hi-standalone-price" />
            </Field>
            <p className="text-[11px] text-slate-500 italic">Standalone Home Internet isn't restricted to a specific plan list.</p>
          </div>
        )}
      </div>

      {/* Bundle */}
      <div className="bg-white border border-slate-200 mb-3" data-testid="hi-bundle">
        <ScenarioHeader title="Smartphone bundle" enabled={value.bundle.enabled} onToggle={(v) => setBundle({ enabled: v })} accent="amber" />
        {value.bundle.enabled && (
          <div className="p-4 border-t border-slate-200 space-y-3">
            <p className="text-[11px] text-slate-500 italic">Requires at least one phone line on the same quote. Capped at 1 promo Home Internet per account.</p>
            <Field label="Device price ($)">
              <input type="number" step="0.01" className="input-sharp max-w-xs" value={value.bundle.price}
                onChange={(e) => setBundle({ price: parseFloat(e.target.value || 0) })}
                data-testid="hi-bundle-price" />
            </Field>
          </div>
        )}
      </div>
    </div>
  );
}


/* ---------------- Categories ---------------- */
const CATEGORY_EMPTY = { name: "", sort_order: 50, active: true };

function CategoriesSection() {
  const can = useCan();
  const canManage = can("categories", "manage");
  const canDelete = can("categories", "delete");
  const [items, setItems] = useState([]); const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); const [open, setOpen] = useState(false); const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/categories"); setItems(data); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const onSave = async () => {
    setBusy(true);
    try {
      const p = { ...editing }; delete p.id; delete p.created_at;
      if (editing.id) await api.put(`/categories/${editing.id}`, p);
      else await api.post("/categories", p);
      toast.success("Category saved"); setOpen(false); setEditing(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this category? Devices in this category will become uncategorized.")) return;
    try { await api.delete(`/categories/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <SectionShell title="Categories" onAdd={canManage ? () => { setEditing({ ...CATEGORY_EMPTY }); setOpen(true); } : undefined} addTestId="add-category">
      {loading ? <Loading /> : items.length === 0 ? <Empty label="No categories yet." /> : (
        <Table headers={["Name", "Sort", "Active", ""]}>
          {items.map((c) => (
            <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`category-row-${c.id}`}>
              <Td><span className="font-semibold text-[#0B132B]">{c.name}</span></Td>
              <Td className="mono">{c.sort_order}</Td>
              <Td><Active on={c.active} /></Td>
              <Td className="text-right">
                <RowActions onEdit={() => { setEditing({ ...c }); setOpen(true); }} onDelete={() => onDelete(c.id)} testKey={c.id} canEdit={canManage} canDelete={canDelete} />
              </Td>
            </tr>
          ))}
        </Table>
      )}

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditing(null); }}>
        <DialogContent className="rounded-none" data-testid="category-dialog">
          <DialogHeader><DialogTitle>{editing?.id ? "Edit category" : "New category"}</DialogTitle></DialogHeader>
          {editing && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Name" full><input className="input-sharp" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="category-field-name" /></Field>
              <Field label="Sort order"><input type="number" className="input-sharp" value={editing.sort_order} onChange={(e) => setEditing({ ...editing, sort_order: parseInt(e.target.value || 0, 10) })} data-testid="category-field-sort" /></Field>
              <Toggle label="Active" v={editing.active} onChange={(v) => setEditing({ ...editing, active: v })} />
            </div>
          )}
          <DialogFooter>
            <button onClick={() => setOpen(false)} className="btn-outline-navy text-sm !py-2.5">Cancel</button>
            <button onClick={onSave} disabled={busy || !editing?.name} className="btn-brand text-sm !py-2.5" data-testid="category-save">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Save</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </SectionShell>
  );
}

/* ---------------- Promos ---------------- */
const PROMO_EMPTY = { title: "", description: "", device_id: "", phone_pricing: null, tablet_pricing: null, home_internet_pricing: null, hotspot_pricing: null, starts_at: "", ends_at: "", active: true };

function anyScenarioEnabled(pp) {
  if (!pp) return false;
  if (pp.finance?.enabled) return true;
  if (pp.full_retail?.enabled) return true;
  for (const sc of ["switch", "join", "no_hassle"]) {
    const g = pp[sc] || {};
    if (g.single_month?.enabled || g.multi_month?.enabled || g.multi_line?.enabled) return true;
  }
  return false;
}

function anyTabletEnabled(tp) {
  if (!tp) return false;
  return !!(tp.standalone?.enabled || tp.bundle?.enabled);
}

function anyHomeInternetEnabled(hp) {
  if (!hp) return false;
  return !!(hp.standalone?.enabled || hp.bundle?.enabled);
}

function PromosSection() {
  const can = useCan();
  const canManage = can("promos", "manage");
  const canDelete = can("promos", "delete");
  const [items, setItems] = useState([]); const [devices, setDevices] = useState([]); const [carriers, setCarriers] = useState([]); const [categories, setCategories] = useState([]); const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); const [open, setOpen] = useState(false); const [busy, setBusy] = useState(false);
  // Column filters. `active` accepts "active" / "inactive" / "" for any. Strings are case-insensitive substring matches.
  const [filters, setFilters] = useState({ title: "", device: "", scenarios: "", starts: "", ends: "", active: "" });
  const setFilter = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  const anyFilter = filters.title || filters.device || filters.scenarios || filters.starts || filters.ends || filters.active;

  const load = async () => {
    setLoading(true);
    try {
      const [p, d, c, cat, pl] = await Promise.all([
        api.get("/promos"),
        api.get("/devices"),
        api.get("/carriers", { params: { active_only: true } }),
        api.get("/categories"),
        api.get("/plans"),
      ]);
      setItems(p.data); setDevices(d.data); setCarriers(c.data); setCategories(cat.data); setPlans(pl.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const devMap = useMemo(() => { const m = {}; devices.forEach((d) => (m[d.id] = d)); return m; }, [devices]);
  const catMap = useMemo(() => { const m = {}; categories.forEach((c) => (m[c.id] = c)); return m; }, [categories]);
  const selectedDevice = editing?.device_id ? devMap[editing.device_id] : null;
  const selectedCategoryName = (selectedDevice && catMap[selectedDevice.category_id]?.name || "").toLowerCase();
  const isPhonePromo = selectedCategoryName === "phones";
  const isTabletPromo = selectedCategoryName === "tablets";
  const isHomeInternetPromo = selectedCategoryName === "home internet";
  const isHotspotPromo = selectedCategoryName === "hotspot" || selectedCategoryName === "hotspots";

  // Plans sharing the selected device's category — used to filter the qualifying-plan picker.
  const eligiblePlans = useMemo(() => {
    if (!selectedDevice?.category_id) return [];
    return plans.filter((p) => p.category_id === selectedDevice.category_id);
  }, [plans, selectedDevice]);

  const onSave = async () => {
    setBusy(true);
    try {
      const p = { ...editing };
      if (!p.device_id) p.device_id = null;
      if (!p.starts_at) p.starts_at = null;
      if (!p.ends_at) p.ends_at = null;
      // Only persist the pricing block that matches the selected device's category.
      if (!isPhonePromo) p.phone_pricing = null;
      else if (!anyScenarioEnabled(p.phone_pricing)) p.phone_pricing = null;
      if (!isTabletPromo) p.tablet_pricing = null;
      else if (!anyTabletEnabled(p.tablet_pricing)) p.tablet_pricing = null;
      if (!isHomeInternetPromo) p.home_internet_pricing = null;
      else if (!anyHomeInternetEnabled(p.home_internet_pricing)) p.home_internet_pricing = null;
      if (!isHotspotPromo) p.hotspot_pricing = null;
      else if (!p.hotspot_pricing?.enabled) p.hotspot_pricing = null;
      delete p.id; delete p.created_at;
      if (editing.id) await api.put(`/promos/${editing.id}`, p);
      else await api.post("/promos", p);
      toast.success("Promo saved"); setOpen(false); setEditing(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const onDelete = async (id) => {
    if (!window.confirm("Delete this promo?")) return;
    try { await api.delete(`/promos/${id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const onDuplicate = async (promo) => {
    try {
      const clone = JSON.parse(JSON.stringify(promo));
      delete clone.id; delete clone.created_at;
      clone.title = `Copy of ${promo.title}`;
      // Leave the new promo INACTIVE so admin reviews before exposing it.
      clone.active = false;
      await api.post("/promos", clone);
      toast.success(`Duplicated "${promo.title}"`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not duplicate");
    }
  };

  const summarizePromo = (p) => {
    const bits = [];
    const pp = p.phone_pricing;
    if (pp) {
      if (pp.finance?.enabled) bits.push("Finance");
      if (pp.full_retail?.enabled) bits.push("Full Retail");
      for (const sc of ["switch", "join", "no_hassle"]) {
        const g = pp[sc] || {};
        const subs = ["single_month", "multi_month", "multi_line"].filter((s) => g[s]?.enabled);
        if (subs.length) bits.push(`${sc.replace("_", " ")} (${subs.length})`);
      }
    }
    const tp = p.tablet_pricing;
    if (tp) {
      if (tp.standalone?.enabled) bits.push("Tablet · Standalone");
      if (tp.bundle?.enabled) bits.push("Tablet · Bundle");
    }
    const hp = p.home_internet_pricing;
    if (hp) {
      if (hp.standalone?.enabled) bits.push("Home Internet · Standalone");
      if (hp.bundle?.enabled) bits.push("Home Internet · Bundle");
    }
    return bits.length ? bits.join(" · ") : "—";
  };

  // Format a "date-only" ISO string as a local-calendar date. `new Date("YYYY-MM-DD")` is parsed
  // as UTC midnight, so in any negative-offset timezone (US Eastern etc.) `toLocaleDateString()`
  // renders the day BEFORE. Slice the date portion and reformat in local context to avoid this.
  const fmtPromoDate = (iso) => {
    if (!iso) return "—";
    const ymd = String(iso).slice(0, 10);
    const [y, m, d] = ymd.split("-").map((s) => parseInt(s, 10));
    if (!y || !m || !d) return "—";
    const dt = new Date(y, m - 1, d);
    return dt.toLocaleDateString();
  };

  // Filter + alphabetical sort (by title, locale-aware case-insensitive). Sort runs after
  // filtering so the visible rows are always alphabetical.
  const filteredItems = useMemo(() => {
    const deviceLabel = (p) => {
      if (!p.device_id) return "generic";
      const d = devMap[p.device_id];
      return d ? `${d.brand} ${d.model}`.toLowerCase() : "";
    };
    const startsLabel = (p) => p.starts_at ? fmtPromoDate(p.starts_at).toLowerCase() : "";
    const endsLabel = (p) => p.ends_at ? fmtPromoDate(p.ends_at).toLowerCase() : "";
    const f = filters;
    const matches = items.filter((p) => {
      if (f.title && !(p.title || "").toLowerCase().includes(f.title.toLowerCase())) return false;
      if (f.device && !deviceLabel(p).includes(f.device.toLowerCase())) return false;
      if (f.scenarios && !summarizePromo(p).toLowerCase().includes(f.scenarios.toLowerCase())) return false;
      if (f.starts && !startsLabel(p).includes(f.starts.toLowerCase())) return false;
      if (f.ends && !endsLabel(p).includes(f.ends.toLowerCase())) return false;
      if (f.active === "active" && !p.active) return false;
      if (f.active === "inactive" && p.active) return false;
      return true;
    });
    return matches.sort((a, b) =>
      (a.title || "").localeCompare(b.title || "", undefined, { sensitivity: "base" })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, filters, devMap]);

  return (
    <SectionShell
      title="Promotions"
      onAdd={canManage ? () => { setEditing({ ...PROMO_EMPTY }); setOpen(true); } : undefined}
      addTestId="add-promo"
      extra={canManage ? (
        <div className="flex gap-2">
          <CsvSampleDownloadButton
            filename="promos_sample.csv"
            headers={PROMO_CSV_HEADERS}
            sampleRow={PROMO_CSV_SAMPLE_ROW()}
          />
          <PromoCsvImportButton onDone={load} />
        </div>
      ) : null}
    >
      {loading ? <Loading /> : items.length === 0 ? <Empty label="No promotions yet." /> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="promos-table">
            <thead>
              <tr className="text-left border-b border-slate-200">
                {["Title", "Device", "Scenarios enabled", "Starts", "Ends", "Active", ""].map((h, i) => (
                  <th key={i} className="tw-overline py-3 px-4">{h}</th>
                ))}
              </tr>
              <tr className="border-b border-slate-200 bg-slate-50">
                <th className="px-4 py-2"><input className="input-sharp !py-1.5 text-xs w-full" placeholder="Filter title…" value={filters.title} onChange={(e) => setFilter("title", e.target.value)} data-testid="promo-filter-title" /></th>
                <th className="px-4 py-2"><input className="input-sharp !py-1.5 text-xs w-full" placeholder="Filter device…" value={filters.device} onChange={(e) => setFilter("device", e.target.value)} data-testid="promo-filter-device" /></th>
                <th className="px-4 py-2"><input className="input-sharp !py-1.5 text-xs w-full" placeholder="Filter scenarios…" value={filters.scenarios} onChange={(e) => setFilter("scenarios", e.target.value)} data-testid="promo-filter-scenarios" /></th>
                <th className="px-4 py-2"><input className="input-sharp !py-1.5 text-xs w-full" placeholder="Filter date…" value={filters.starts} onChange={(e) => setFilter("starts", e.target.value)} data-testid="promo-filter-starts" /></th>
                <th className="px-4 py-2"><input className="input-sharp !py-1.5 text-xs w-full" placeholder="Filter date…" value={filters.ends} onChange={(e) => setFilter("ends", e.target.value)} data-testid="promo-filter-ends" /></th>
                <th className="px-4 py-2">
                  <select className="input-sharp !py-1.5 text-xs w-full" value={filters.active} onChange={(e) => setFilter("active", e.target.value)} data-testid="promo-filter-active">
                    <option value="">All</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </th>
                <th className="px-4 py-2 text-right">
                  {anyFilter && (
                    <button
                      type="button"
                      onClick={() => setFilters({ title: "", device: "", scenarios: "", starts: "", ends: "", active: "" })}
                      className="text-[10px] mono uppercase tracking-widest text-[#008B99] hover:text-[#0B132B] underline"
                      data-testid="promo-filter-clear"
                    >Clear</button>
                  )}
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredItems.length === 0 ? (
                <tr><td colSpan={7} className="py-10 text-center text-slate-500" data-testid="promo-table-empty">No promotions match the current filters.</td></tr>
              ) : filteredItems.map((p) => (
                <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`promo-row-${p.id}`}>
                  <Td><span className="font-semibold text-[#0B132B]">{p.title}</span></Td>
                  <Td>{p.device_id ? `${devMap[p.device_id]?.brand || ""} ${devMap[p.device_id]?.model || "—"}` : <span className="text-slate-400">Generic</span>}</Td>
                  <Td className="text-xs text-slate-600">{summarizePromo(p)}</Td>
                  <Td className="mono text-xs text-slate-500">{fmtPromoDate(p.starts_at)}</Td>
                  <Td className="mono text-xs text-slate-500">{fmtPromoDate(p.ends_at)}</Td>
                  <Td><Active on={p.active} /></Td>
                  <Td className="text-right">
                    <RowActions
                      onEdit={() => {
                        setEditing({
                          ...PROMO_EMPTY,
                          ...p,
                          device_id: p.device_id || "",
                          starts_at: p.starts_at || "",
                          ends_at: p.ends_at || "",
                          phone_pricing: p.phone_pricing ? mergePhonePricing(p.phone_pricing) : null,
                          tablet_pricing: p.tablet_pricing ? mergeTabletPricing(p.tablet_pricing) : null,
                          home_internet_pricing: p.home_internet_pricing ? mergeHomeInternetPricing(p.home_internet_pricing) : null,
                          hotspot_pricing: p.hotspot_pricing || null,
                        });
                        setOpen(true);
                      }}
                      onDuplicate={() => onDuplicate(p)}
                      onDelete={() => onDelete(p.id)}
                      testKey={p.id}
                      canEdit={canManage}
                      canDelete={canDelete}
                    />
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditing(null); }}>
        <DialogContent className="max-w-3xl rounded-none max-h-[90vh] overflow-y-auto" data-testid="promo-dialog">
          <DialogHeader><DialogTitle>{editing?.id ? "Edit promo" : "New promo"}</DialogTitle></DialogHeader>
          {editing && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Title" full><input className="input-sharp" value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} data-testid="promo-field-title" /></Field>
              <Field label="Description" full><textarea className="input-sharp h-20" value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} /></Field>
              <Field label="Device (optional)" full>
                <select className="input-sharp" value={editing.device_id} onChange={(e) => {
                  const next = { ...editing, device_id: e.target.value };
                  // If user changes the device, reset phone_pricing scaffold so editor renders fresh state
                  if (e.target.value !== editing.device_id) {
                    next.phone_pricing = next.phone_pricing || null;
                  }
                  setEditing(next);
                }} data-testid="promo-field-device">
                  <option value="">— None (generic) —</option>
                  {devices.map((d) => <option key={d.id} value={d.id}>{d.brand} {d.model}</option>)}
                </select>
              </Field>
              <Field label="Starts at"><input type="date" className="input-sharp" value={editing.starts_at ? editing.starts_at.slice(0,10) : ""} onChange={(e) => setEditing({ ...editing, starts_at: e.target.value ? `${e.target.value}T12:00:00.000Z` : "" })} data-testid="promo-field-starts-at" /></Field>
              <Field label="Ends at"><input type="date" className="input-sharp" value={editing.ends_at ? editing.ends_at.slice(0,10) : ""} onChange={(e) => setEditing({ ...editing, ends_at: e.target.value ? `${e.target.value}T12:00:00.000Z` : "" })} data-testid="promo-field-ends-at" /></Field>
              <Toggle label="Active" v={editing.active} onChange={(v) => setEditing({ ...editing, active: v })} />
              {isPhonePromo && (
                <PhonePricingEditor
                  value={editing.phone_pricing ? mergePhonePricing(editing.phone_pricing) : JSON.parse(JSON.stringify(EMPTY_PHONE_PRICING))}
                  onChange={(pp) => setEditing({ ...editing, phone_pricing: pp })}
                  carriers={carriers}
                  eligiblePlans={eligiblePlans}
                />
              )}
              {isTabletPromo && (
                <TabletPricingEditor
                  value={editing.tablet_pricing ? mergeTabletPricing(editing.tablet_pricing) : JSON.parse(JSON.stringify(EMPTY_TABLET_PRICING))}
                  onChange={(tp) => setEditing({ ...editing, tablet_pricing: tp })}
                  eligiblePlans={eligiblePlans}
                />
              )}
              {isHomeInternetPromo && (
                <HomeInternetPricingEditor
                  value={editing.home_internet_pricing ? mergeHomeInternetPricing(editing.home_internet_pricing) : JSON.parse(JSON.stringify(EMPTY_HI_PRICING))}
                  onChange={(hp) => setEditing({ ...editing, home_internet_pricing: hp })}
                />
              )}
              {isHotspotPromo && (
                <HotspotPricingEditor
                  value={editing.hotspot_pricing || { enabled: false, price: 0, eligible_plan_ids: [] }}
                  onChange={(hp) => setEditing({ ...editing, hotspot_pricing: hp })}
                  eligiblePlans={eligiblePlans}
                />
              )}
              {selectedDevice && !isPhonePromo && !isTabletPromo && !isHomeInternetPromo && !isHotspotPromo && (
                <p className="sm:col-span-2 text-xs text-slate-500 italic">No scenario editor available for this device's category. Promo pricing editors exist for <strong>Phones</strong>, <strong>Tablets</strong>, <strong>Home Internet</strong>, and <strong>Hotspots</strong>.</p>
              )}
            </div>
          )}
          <DialogFooter>
            <button onClick={() => setOpen(false)} className="btn-outline-navy text-sm !py-2.5">Cancel</button>
            <button onClick={onSave} disabled={busy} className="btn-brand text-sm !py-2.5" data-testid="promo-save">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Save</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </SectionShell>
  );
}

/* ---------------- Carriers ---------------- */

/* ---------------- Add-ons ---------------- */
const ADDON_EMPTY = { name: "", monthly_price: 0, one_time_price: 0, applies_to_categories: [], sort_order: 50, active: true, charge_first_month_today: false };

function AddOnsSection() {
  const can = useCan();
  const canManage = can("addons", "manage");
  const canDelete = can("addons", "delete");
  const [items, setItems] = useState([]); const [categories, setCategories] = useState([]); const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); const [open, setOpen] = useState(false); const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [a, c] = await Promise.all([api.get("/addons"), api.get("/categories")]);
      setItems(a.data); setCategories(c.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const catMap = useMemo(() => { const m = {}; categories.forEach((c) => (m[c.id] = c)); return m; }, [categories]);

  const onSave = async () => {
    setBusy(true);
    try {
      const p = { ...editing };
      delete p.id; delete p.created_at;
      if (editing.id) await api.put(`/addons/${editing.id}`, p);
      else await api.post("/addons", p);
      toast.success("Add-on saved"); setOpen(false); setEditing(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const onDelete = async (id) => {
    if (!window.confirm("Delete this add-on?")) return;
    try { await api.delete(`/addons/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const toggleCat = (cid) => {
    const cur = editing.applies_to_categories || [];
    setEditing({ ...editing, applies_to_categories: cur.includes(cid) ? cur.filter((x) => x !== cid) : [...cur, cid] });
  };

  return (
    <SectionShell title="Add-ons" onAdd={canManage ? () => { setEditing({ ...ADDON_EMPTY }); setOpen(true); } : undefined} addTestId="add-addon">
      {loading ? <Loading /> : items.length === 0 ? <Empty label="No add-ons yet. Tip: create Auto Pay with monthly_price = -5.00 for a $5 auto-pay discount." /> : (
        <Table headers={["Name", "Monthly", "Applies to", "Sort", "Active", ""]}>
          {items.map((a) => {
            const isDiscount = (a.monthly_price || 0) < 0;
            return (
              <tr key={a.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`addon-row-${a.id}`}>
                <Td><span className="font-semibold text-[#0B132B]">{a.name}</span></Td>
                <Td className={`mono ticker-num font-bold ${isDiscount ? "text-[#008B99]" : "text-[#0B132B]"}`}>
                  {isDiscount ? "−" : ""}${Math.abs(a.monthly_price || 0).toFixed(2)}/mo
                </Td>
                <Td className="text-xs text-slate-600">
                  {(a.applies_to_categories || []).length === 0
                    ? <span className="badge-pill badge-outline text-[10px]">Account</span>
                    : (a.applies_to_categories || []).map((cid) => (
                        <span key={cid} className="badge-pill badge-outline text-[10px] mr-1">{catMap[cid]?.name || cid}</span>
                      ))}
                </Td>
                <Td className="mono text-xs text-slate-500">{a.sort_order}</Td>
                <Td><Active on={a.active} /></Td>
                <Td className="text-right">
                  <RowActions
                    onEdit={() => { setEditing({ ...ADDON_EMPTY, ...a, applies_to_categories: a.applies_to_categories || [] }); setOpen(true); }}
                    onDelete={() => onDelete(a.id)}
                    testKey={a.id}
                    canEdit={canManage}
                    canDelete={canDelete}
                  />
                </Td>
              </tr>
            );
          })}
        </Table>
      )}

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditing(null); }}>
        <DialogContent className="max-w-xl rounded-none" data-testid="addon-dialog">
          <DialogHeader><DialogTitle>{editing?.id ? "Edit add-on" : "New add-on"}</DialogTitle></DialogHeader>
          {editing && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Name" full>
                <input className="input-sharp" value={editing.name}
                  onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                  data-testid="addon-field-name" placeholder="e.g. Auto Pay, Device Protection $5" />
              </Field>
              <Field label="Monthly price ($)">
                <input type="number" step="0.01" className="input-sharp" value={editing.monthly_price}
                  onChange={(e) => setEditing({ ...editing, monthly_price: parseFloat(e.target.value || 0) })}
                  data-testid="addon-field-price" />
                <p className="text-[11px] text-slate-500 mt-1 italic">Use a negative number for discounts (e.g. <code>-5</code> for Auto Pay).</p>
              </Field>
              <Field label="One-time charge today ($)">
                <input type="number" step="0.01" min="0" className="input-sharp" value={editing.one_time_price ?? 0}
                  onChange={(e) => setEditing({ ...editing, one_time_price: parseFloat(e.target.value || 0) })}
                  data-testid="addon-field-one-time" />
                <p className="text-[11px] text-slate-500 mt-1 italic">For sign-up fees like SIM kit ($5). Leave monthly price at $0 for a today-only add-on.</p>
              </Field>
              <Field label="Sort order">
                <input type="number" className="input-sharp" value={editing.sort_order}
                  onChange={(e) => setEditing({ ...editing, sort_order: parseInt(e.target.value || 50, 10) })} />
              </Field>
              <div className="sm:col-span-2 border border-slate-200 p-4" data-testid="addon-categories-picker">
                <div className="tw-overline">Applies to categories</div>
                <p className="text-[11px] text-slate-500 mt-1 mb-3">Leave all unchecked = account-wide (charged once). Selecting categories multiplies the price by the # of lines in those categories.</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {categories.map((c) => {
                    const sel = (editing.applies_to_categories || []).includes(c.id);
                    return (
                      <button key={c.id} type="button" onClick={() => toggleCat(c.id)}
                        className={`text-left px-3 py-2 border-2 text-sm transition-all ${sel ? "border-[#00E5FF] bg-[#00E5FF]/10" : "border-slate-200 hover:border-slate-400"}`}
                        data-testid={`addon-cat-${c.id}`}>
                        <span className="flex items-center justify-between gap-2">
                          <span className="font-medium text-[#0B132B]">{c.name}</span>
                          <span className={`h-4 w-4 grid place-items-center border-2 ${sel ? "border-[#00E5FF] bg-[#00E5FF]" : "border-slate-300"}`}>
                            {sel && <span className="text-[10px] text-[#0B132B]">✓</span>}
                          </span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
              <Toggle label="Active" v={editing.active} onChange={(v) => setEditing({ ...editing, active: v })} />
              <Toggle
                label="Charge first month today (TWP+ / device protection style)"
                v={!!editing.charge_first_month_today}
                onChange={(v) => setEditing({ ...editing, charge_first_month_today: v })}
              />
            </div>
          )}
          <DialogFooter>
            <button onClick={() => setOpen(false)} className="btn-outline-navy text-sm !py-2.5">Cancel</button>
            <button onClick={onSave} disabled={busy} className="btn-brand text-sm !py-2.5" data-testid="addon-save">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Save
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </SectionShell>
  );
}

const CARRIER_EMPTY = { name: "", logo_url: "", active: true, is_port_in_eligible: false };

function CarriersSection() {
  const can = useCan();
  const canManage = can("carriers", "manage");
  const canDelete = can("carriers", "delete");
  const [items, setItems] = useState([]); const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); const [open, setOpen] = useState(false); const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/carriers"); setItems(data); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const onSave = async () => {
    setBusy(true);
    try {
      const p = { ...editing }; delete p.id; delete p.created_at;
      if (editing.id) await api.put(`/carriers/${editing.id}`, p);
      else await api.post("/carriers", p);
      toast.success("Carrier saved"); setOpen(false); setEditing(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this carrier? Any promos using it will lose this carrier from their eligibility list.")) return;
    try { await api.delete(`/carriers/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <SectionShell title="Port-in carriers" onAdd={canManage ? () => { setEditing({ ...CARRIER_EMPTY }); setOpen(true); } : undefined} addTestId="add-carrier">
      {loading ? <Loading /> : items.length === 0 ? <Empty label="No carriers yet." /> : (
        <Table headers={["Name", "Logo", "Port-in eligible", "Active", ""]}>
          {items.map((c) => (
            <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`carrier-row-${c.id}`}>
              <Td><span className="font-semibold text-[#0B132B]">{c.name}</span></Td>
              <Td>{c.logo_url ? <img src={c.logo_url} alt={c.name} className="h-6 max-w-[80px] object-contain" /> : <span className="text-slate-300 text-xs">—</span>}</Td>
              <Td data-testid={`carrier-eligible-${c.id}`}>
                {c.is_port_in_eligible
                  ? <span className="inline-block px-2 py-0.5 text-[10px] font-bold tracking-widest uppercase bg-[#00E5FF] text-[#0B132B]">Yes</span>
                  : <span className="inline-block px-2 py-0.5 text-[10px] font-bold tracking-widest uppercase bg-slate-100 text-slate-500">No</span>}
              </Td>
              <Td><Active on={c.active} /></Td>
              <Td className="text-right">
                <RowActions onEdit={() => { setEditing({ ...c }); setOpen(true); }} onDelete={() => onDelete(c.id)} testKey={c.id} canEdit={canManage} canDelete={canDelete} />
              </Td>
            </tr>
          ))}
        </Table>
      )}

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditing(null); }}>
        <DialogContent className="rounded-none" data-testid="carrier-dialog">
          <DialogHeader><DialogTitle>{editing?.id ? "Edit carrier" : "New carrier"}</DialogTitle></DialogHeader>
          {editing && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Name" full><input className="input-sharp" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="carrier-field-name" /></Field>
              <Field label="Logo URL" full><input className="input-sharp" value={editing.logo_url} onChange={(e) => setEditing({ ...editing, logo_url: e.target.value })} placeholder="https://…" data-testid="carrier-field-logo" /></Field>
              <Toggle
                label="Eligible for port-in promo"
                v={!!editing.is_port_in_eligible}
                onChange={(v) => setEditing({ ...editing, is_port_in_eligible: v })}
                testKey="carrier-field-port-eligible"
              />
              <Toggle label="Active" v={editing.active} onChange={(v) => setEditing({ ...editing, active: v })} />
            </div>
          )}
          <DialogFooter>
            <button onClick={() => setOpen(false)} className="btn-outline-navy text-sm !py-2.5">Cancel</button>
            <button onClick={onSave} disabled={busy || !editing?.name} className="btn-brand text-sm !py-2.5" data-testid="carrier-save">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Save</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </SectionShell>
  );
}

/* ---------------- Analytics ---------------- */
function AnalyticsSection() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/analytics/summary"); setData(data); }
      catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <div className="mt-6 bg-white border border-slate-200 p-10"><Loading /></div>;
  if (!data) return null;

  const T = data.totals;
  const stats = [
    { k: "Quotes total", v: T.quotes, testid: "stat-quotes-total" },
    { k: "Last 24h", v: T.today, testid: "stat-quotes-24h" },
    { k: "Last 7d", v: T.week, testid: "stat-quotes-7d" },
    { k: "Last 30d", v: T.month, testid: "stat-quotes-30d" },
    { k: "Views", v: T.views, testid: "stat-views" },
    { k: "Shares", v: T.shares, testid: "stat-shares" },
    { k: "Pipeline value", v: `$${(T.revenue_estimated || 0).toFixed(0)}`, testid: "stat-pipeline" },
  ];

  return (
    <div className="mt-6 space-y-6" data-testid="analytics-section">
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
        {stats.map((s) => (
          <div key={s.k} className="bg-white border border-slate-200 p-5" data-testid={s.testid}>
            <div className="tw-overline">{s.k}</div>
            <div className="mono text-3xl font-bold text-[#0B132B] mt-2 ticker-num">{s.v}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-5 bg-white border border-slate-200">
          <div className="p-5 border-b border-slate-200"><h3 className="font-bold text-[#0B132B]">Top plans (by quote count)</h3></div>
          <div className="p-2">
            {data.top_plans.length === 0 ? (
              <Empty label="No quotes yet." />
            ) : (
              <Table headers={["Plan", "Quotes", "Pipeline $"]}>
                {data.top_plans.map((p) => (
                  <tr key={p.plan_name} className="border-b border-slate-100" data-testid={`top-plan-${p.plan_name}`}>
                    <Td><span className="font-semibold text-[#0B132B]">{p.plan_name}</span></Td>
                    <Td className="mono">{p.count}</Td>
                    <Td className="mono">${p.revenue.toFixed(0)}</Td>
                  </tr>
                ))}
              </Table>
            )}
          </div>
        </div>

        <div className="lg:col-span-7 bg-white border border-slate-200">
          <div className="p-5 border-b border-slate-200"><h3 className="font-bold text-[#0B132B]">Recent quotes</h3></div>
          <div className="p-2">
            {data.recent_quotes.length === 0 ? (
              <Empty label="No quotes yet." />
            ) : (
              <Table headers={["When", "Plan", "Lines", "Total", "Views", "Shares", ""]}>
                {data.recent_quotes.map((q) => (
                  <tr key={q.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`recent-quote-${q.id}`}>
                    <Td className="mono text-xs text-slate-500">{new Date(q.created_at).toLocaleString()}</Td>
                    <Td><span className="font-semibold text-[#0B132B]">{q.plan_name}</span></Td>
                    <Td className="mono">{q.lines}</Td>
                    <Td className="mono">${(q.total || 0).toFixed(2)}</Td>
                    <Td className="mono inline-flex items-center gap-1"><Eye className="h-3 w-3 text-slate-400" />{q.views || 0}</Td>
                    <Td className="mono inline-flex items-center gap-1"><Share2 className="h-3 w-3 text-slate-400" />{q.shares || 0}</Td>
                    <Td className="text-right"><Link to={`/quote/${q.id}`} target="_blank" className="text-xs text-[#008B99] hover:text-[#0B132B] underline">open</Link></Td>
                  </tr>
                ))}
              </Table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Settings ---------------- */
/* ---------------- Settings (wrapper with sub-tabs) ---------------- */
function SettingsSection() {
  const can = useCan();
  const tabs = [
    { id: "site",    label: "Site Settings",  resource: "site_settings",  Section: SiteSettingsSection },
    { id: "agents",  label: "Master Agents",  resource: "master_agents",  Section: MasterAgentsSection },
    { id: "dealers", label: "Dealers",        resource: "dealers",        Section: DealersSection },
    { id: "stores",  label: "Stores",         resource: "stores",         Section: StoresSection },
  ];
  const visible = tabs.filter((t) => can(t.resource, "view"));
  const [tab, setTab] = useState(visible[0]?.id || "site");
  if (visible.length === 0) {
    return <div className="mt-6 bg-white border border-slate-200 p-8 text-center text-sm text-slate-500">Your role has no access to any settings section.</div>;
  }
  const Active = visible.find((t) => t.id === tab)?.Section || visible[0].Section;
  return (
    <div className="mt-6 space-y-4" data-testid="settings-section">
      <div className="flex gap-2 flex-wrap" data-testid="settings-subtabs">
        {visible.map((t) => (
          <button
            key={t.id}
            className={`text-sm font-semibold px-5 py-2.5 border-2 transition ${tab === t.id ? "border-[#0B132B] bg-[#0B132B] text-white" : "border-slate-200 text-slate-600 hover:border-[#0B132B]"}`}
            onClick={() => setTab(t.id)}
            data-testid={`settings-tab-${t.id}`}
          >
            {t.label}
          </button>
        ))}
      </div>
      <Active />
    </div>
  );
}

function SiteSettingsSection() {
  const can = useCan();
  const canManage = can("site_settings", "manage");
  const [s, setS] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/settings"); setS(data); }
      finally { setLoading(false); }
    })();
  }, []);

  const save = async () => {
    setBusy(true);
    try { await api.put("/settings", s); toast.success("Settings saved"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (loading || !s) return <div className="mt-6 bg-white border border-slate-200 p-10"><Loading /></div>;

  return (
    <div className="mt-6 bg-white border border-slate-200 p-6" data-testid="settings-section">
      <h2 className="text-xl font-bold text-[#0B132B]">Site settings</h2>
      <p className="text-sm text-slate-500 mt-1">Plan prices are <strong>tax-inclusive</strong>. Sales tax is entered per-quote on step 4 of the wizard. <strong>Access charge</strong> applies only when phones are on the bundle ($25 for 1 phone line, $50 for 2+ phone lines by default).</p>
      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3" data-testid="settings-grid">
        <Field label="Access charge — 1 line ($)">
          <input type="number" step="0.01" className="input-sharp" value={s.access_charge_single_line ?? 25}
            onChange={(e) => setS({ ...s, access_charge_single_line: parseFloat(e.target.value || 0) })}
            data-testid="settings-access-charge-single" />
        </Field>
        <Field label="Access charge — 2+ lines ($)">
          <input type="number" step="0.01" className="input-sharp" value={s.access_charge_multi_line ?? 50}
            onChange={(e) => setS({ ...s, access_charge_multi_line: parseFloat(e.target.value || 0) })}
            data-testid="settings-access-charge-multi" />
        </Field>
        <Field label="# of promo tablets allowed">
          <input type="number" step="1" min="0" max="10" className="input-sharp" value={s.tablet_bundle_cap ?? 2}
            onChange={(e) => setS({ ...s, tablet_bundle_cap: parseInt(e.target.value || 0, 10) })}
            data-testid="settings-tablet-bundle-cap" />
        </Field>
        <Field label="# of promo HI allowed">
          <input type="number" step="1" min="0" max="10" className="input-sharp" value={s.home_internet_bundle_cap ?? 1}
            onChange={(e) => setS({ ...s, home_internet_bundle_cap: parseInt(e.target.value || 0, 10) })}
            data-testid="settings-hi-bundle-cap" />
        </Field>
        <Field label="Max # of lines per account">
          <input type="number" step="1" min="1" max="50" className="input-sharp" value={s.max_lines_per_account ?? 10}
            onChange={(e) => setS({ ...s, max_lines_per_account: parseInt(e.target.value || 1, 10) })}
            data-testid="settings-max-lines" />
        </Field>
        <Field
          label="Finance devices per account"
          info="Cap on how many phones in a bundle can be financed. Lines beyond this cap can't pick the Finance scenario."
        >
          <input type="number" step="1" min="0" max="50" className="input-sharp" value={s.allowed_finance_devices_per_account ?? 1}
            onChange={(e) => setS({ ...s, allowed_finance_devices_per_account: parseInt(e.target.value || 0, 10) })}
            data-testid="settings-finance-devices-cap" />
        </Field>
        <Field
          label="Promo phones requiring ID verify"
          info="Phone lines beyond this cap force No Hassle (if port-in or new number) or Full Retail."
        >
          <input type="number" step="1" min="0" max="50" className="input-sharp" value={s.phone_id_verify_cap ?? 4}
            onChange={(e) => setS({ ...s, phone_id_verify_cap: parseInt(e.target.value || 0, 10) })}
            data-testid="settings-phone-id-verify-cap" />
        </Field>
        <Field
          label="Print · Top margin (in)"
          info="Whitespace at the top of the printed quote. Uses real CSS padding so it can't be overridden by the browser print dialog."
        >
          <input type="number" step="0.05" min="0" max="3" className="input-sharp" value={s.print_margin_top_in ?? 1}
            onChange={(e) => setS({ ...s, print_margin_top_in: parseFloat(e.target.value || 0) })}
            data-testid="settings-print-margin-top" />
        </Field>
        <Field label="Print · Right margin (in)">
          <input type="number" step="0.05" min="0" max="3" className="input-sharp" value={s.print_margin_right_in ?? 1}
            onChange={(e) => setS({ ...s, print_margin_right_in: parseFloat(e.target.value || 0) })}
            data-testid="settings-print-margin-right" />
        </Field>
        <Field label="Print · Bottom margin (in)">
          <input type="number" step="0.05" min="0" max="3" className="input-sharp" value={s.print_margin_bottom_in ?? 0.75}
            onChange={(e) => setS({ ...s, print_margin_bottom_in: parseFloat(e.target.value || 0) })}
            data-testid="settings-print-margin-bottom" />
        </Field>
        <Field label="Print · Left margin (in)">
          <input type="number" step="0.05" min="0" max="3" className="input-sharp" value={s.print_margin_left_in ?? 1}
            onChange={(e) => setS({ ...s, print_margin_left_in: parseFloat(e.target.value || 0) })}
            data-testid="settings-print-margin-left" />
        </Field>
        <Field label="Currency">
          <input className="input-sharp" value={s.currency} onChange={(e) => setS({ ...s, currency: e.target.value })} data-testid="settings-currency" />
        </Field>
        <Field label="Company name">
          <input className="input-sharp" value={s.company_name} onChange={(e) => setS({ ...s, company_name: e.target.value })} data-testid="settings-company" />
        </Field>
      </div>
      <div className="mt-6">
        {canManage ? (
          <button onClick={save} disabled={busy} className="btn-brand !py-2.5 text-sm" data-testid="settings-save">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Save settings
          </button>
        ) : (
          <p className="text-xs text-slate-500 italic" data-testid="settings-readonly-note">Read-only — your role does not include site_settings:manage.</p>
        )}
      </div>
    </div>
  );
}

/* ---------------- Master Agents / Dealers / Stores ---------------- */
function MasterAgentsSection() {
  const can = useCan();
  const canManage = can("master_agents", "manage");
  const canDelete = can("master_agents", "delete");
  const [items, setItems] = useState([]); const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); const [open, setOpen] = useState(false); const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/master-agents"); setItems(data || []); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const onSave = async () => {
    setBusy(true);
    try {
      const body = { name: editing.name, active: editing.active };
      if (editing.id) await api.put(`/master-agents/${editing.id}`, body);
      else await api.post("/master-agents", body);
      toast.success("Master agent saved");
      setOpen(false); setEditing(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this master agent?")) return;
    try { await api.delete(`/master-agents/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <SectionShell
      title="Master Agents"
      onAdd={canManage ? () => { setEditing({ id: null, name: "", active: true }); setOpen(true); } : undefined}
      addTestId="add-master-agent"
    >
      {loading ? <Loading /> : items.length === 0 ? <Empty label="No master agents yet." /> : (
        <Table headers={["Name", "Status", "Created", ""]}>
          {items.map((m) => (
            <tr key={m.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`master-agent-row-${m.id}`}>
              <Td><span className="font-semibold text-[#0B132B]">{m.name}</span></Td>
              <Td><Active on={m.active} /></Td>
              <Td className="mono text-xs text-slate-500">{new Date(m.created_at).toLocaleDateString()}</Td>
              <Td className="text-right">
                <RowActions
                  onEdit={() => { setEditing({ ...m }); setOpen(true); }}
                  onDelete={() => onDelete(m.id)}
                  testKey={m.id}
                  canEdit={canManage}
                  canDelete={canDelete}
                />
              </Td>
            </tr>
          ))}
        </Table>
      )}

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditing(null); }}>
        <DialogContent className="rounded-none" data-testid="master-agent-dialog">
          <DialogHeader><DialogTitle>{editing?.id ? "Edit master agent" : "New master agent"}</DialogTitle></DialogHeader>
          {editing && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Name" full>
                <input className="input-sharp" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="master-agent-field-name" />
              </Field>
              <Toggle label="Active" v={editing.active} onChange={(v) => setEditing({ ...editing, active: v })} />
            </div>
          )}
          <DialogFooter>
            <button onClick={() => setOpen(false)} className="btn-outline-navy text-sm !py-2.5">Cancel</button>
            <button onClick={onSave} disabled={busy || !editing?.name} className="btn-brand text-sm !py-2.5" data-testid="master-agent-save">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} {editing?.id ? "Save" : "Create"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </SectionShell>
  );
}

function DealersSection() {
  const can = useCan();
  const canManage = can("dealers", "manage");
  const canDelete = can("dealers", "delete");
  const [items, setItems] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); const [open, setOpen] = useState(false); const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [d, a] = await Promise.all([api.get("/dealers"), api.get("/master-agents").catch(() => ({ data: [] }))]);
      setItems(d.data || []); setAgents(a.data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const onSave = async () => {
    setBusy(true);
    try {
      const body = { name: editing.name, master_agent_id: editing.master_agent_id, active: editing.active };
      if (editing.id) await api.put(`/dealers/${editing.id}`, body);
      else await api.post("/dealers", body);
      toast.success("Dealer saved");
      setOpen(false); setEditing(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this dealer?")) return;
    try { await api.delete(`/dealers/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const agentName = (id) => agents.find((a) => a.id === id)?.name || <span className="text-slate-300">— removed —</span>;
  const canSave = editing && editing.name && editing.master_agent_id;

  return (
    <SectionShell
      title="Dealers"
      onAdd={canManage ? () => { setEditing({ id: null, name: "", master_agent_id: agents[0]?.id || "", active: true }); setOpen(true); } : undefined}
      addTestId="add-dealer"
    >
      {loading ? <Loading /> : items.length === 0 ? <Empty label="No dealers yet. Add a master agent first if none exist." /> : (
        <Table headers={["Name", "Master agent", "Status", "Created", ""]}>
          {items.map((d) => (
            <tr key={d.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`dealer-row-${d.id}`}>
              <Td><span className="font-semibold text-[#0B132B]">{d.name}</span></Td>
              <Td className="text-sm text-slate-700">{agentName(d.master_agent_id)}</Td>
              <Td><Active on={d.active} /></Td>
              <Td className="mono text-xs text-slate-500">{new Date(d.created_at).toLocaleDateString()}</Td>
              <Td className="text-right">
                <RowActions
                  onEdit={() => { setEditing({ ...d }); setOpen(true); }}
                  onDelete={() => onDelete(d.id)}
                  testKey={d.id}
                  canEdit={canManage}
                  canDelete={canDelete}
                />
              </Td>
            </tr>
          ))}
        </Table>
      )}

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditing(null); }}>
        <DialogContent className="rounded-none" data-testid="dealer-dialog">
          <DialogHeader><DialogTitle>{editing?.id ? "Edit dealer" : "New dealer"}</DialogTitle></DialogHeader>
          {editing && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Name">
                <input className="input-sharp" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="dealer-field-name" />
              </Field>
              <Field
                label="Master agent"
                info={editing?.id ? "Changing this transfers the dealer (and all its stores) to a different master agent." : undefined}
              >
                <select className="input-sharp" value={editing.master_agent_id || ""} onChange={(e) => setEditing({ ...editing, master_agent_id: e.target.value })} data-testid="dealer-field-master-agent">
                  <option value="">— Select —</option>
                  {agents.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
                {agents.length === 0 && <p className="text-[11px] text-slate-500 mt-1 italic">No master agents defined. Create one under the Master Agents tab first.</p>}
              </Field>
              <Toggle label="Active" v={editing.active} onChange={(v) => setEditing({ ...editing, active: v })} />
            </div>
          )}
          <DialogFooter>
            <button onClick={() => setOpen(false)} className="btn-outline-navy text-sm !py-2.5">Cancel</button>
            <button onClick={onSave} disabled={busy || !canSave} className="btn-brand text-sm !py-2.5" data-testid="dealer-save">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} {editing?.id ? "Save" : "Create"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </SectionShell>
  );
}

function StoresSection() {
  const can = useCan();
  const canManage = can("stores", "manage");
  const canDelete = can("stores", "delete");
  const [items, setItems] = useState([]);
  const [dealers, setDealers] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); const [open, setOpen] = useState(false); const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [s, d, a] = await Promise.all([
        api.get("/stores"),
        api.get("/dealers").catch(() => ({ data: [] })),
        api.get("/master-agents").catch(() => ({ data: [] })),
      ]);
      setItems(s.data || []); setDealers(d.data || []); setAgents(a.data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const onSave = async () => {
    setBusy(true);
    try {
      const body = {
        name: editing.name,
        address: editing.address || "",
        phone: editing.phone || "",
        email: editing.email || "",
        dealer_id: editing.dealer_id,
        active: editing.active,
      };
      if (editing.id) await api.put(`/stores/${editing.id}`, body);
      else await api.post("/stores", body);
      toast.success("Store saved");
      setOpen(false); setEditing(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this store?")) return;
    try { await api.delete(`/stores/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const dealerById = (id) => dealers.find((d) => d.id === id);
  const dealerName = (id) => dealerById(id)?.name || <span className="text-slate-300">— removed —</span>;
  const agentNameForDealer = (id) => {
    const d = dealerById(id);
    if (!d) return null;
    return agents.find((a) => a.id === d.master_agent_id)?.name;
  };

  const canSave = editing && editing.name && editing.dealer_id;

  return (
    <SectionShell
      title="Stores"
      onAdd={canManage ? () => { setEditing({ id: null, name: "", address: "", phone: "", email: "", dealer_id: dealers[0]?.id || "", active: true }); setOpen(true); } : undefined}
      addTestId="add-store"
    >
      {loading ? <Loading /> : items.length === 0 ? <Empty label="No stores yet. Add a dealer first if none exist." /> : (
        <Table headers={["Name", "Address", "Phone", "Email", "Dealer / Master agent", "Status", ""]}>
          {items.map((s) => (
            <tr key={s.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`store-row-${s.id}`}>
              <Td><span className="font-semibold text-[#0B132B]">{s.name}</span></Td>
              <Td className="text-xs text-slate-600 max-w-[200px] truncate" title={s.address}>{s.address || <span className="text-slate-300">—</span>}</Td>
              <Td className="mono text-xs">{s.phone || <span className="text-slate-300">—</span>}</Td>
              <Td className="mono text-xs">{s.email || <span className="text-slate-300">—</span>}</Td>
              <Td className="text-xs">
                <div className="font-semibold text-[#0B132B]">{dealerName(s.dealer_id)}</div>
                <div className="text-slate-500">{agentNameForDealer(s.dealer_id) || "—"}</div>
              </Td>
              <Td><Active on={s.active} /></Td>
              <Td className="text-right">
                <RowActions
                  onEdit={() => { setEditing({ ...s }); setOpen(true); }}
                  onDelete={() => onDelete(s.id)}
                  testKey={s.id}
                  canEdit={canManage}
                  canDelete={canDelete}
                />
              </Td>
            </tr>
          ))}
        </Table>
      )}

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditing(null); }}>
        <DialogContent className="rounded-none max-w-2xl" data-testid="store-dialog">
          <DialogHeader><DialogTitle>{editing?.id ? "Edit store" : "New store"}</DialogTitle></DialogHeader>
          {editing && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Store name" full>
                <input className="input-sharp" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="store-field-name" />
              </Field>
              <Field label="Address" full>
                <input className="input-sharp" value={editing.address} onChange={(e) => setEditing({ ...editing, address: e.target.value })} placeholder="Street, City, State ZIP" data-testid="store-field-address" />
              </Field>
              <Field label="Phone">
                <input className="input-sharp" value={editing.phone} onChange={(e) => setEditing({ ...editing, phone: e.target.value })} placeholder="555-123-4567" data-testid="store-field-phone" />
              </Field>
              <Field label="Email">
                <input type="email" className="input-sharp" value={editing.email} onChange={(e) => setEditing({ ...editing, email: e.target.value })} placeholder="store@example.com" data-testid="store-field-email" />
              </Field>
              <Field
                label="Dealer"
                info={editing?.id ? "Changing this transfers the store to a different dealer (preserves historical quote attribution)." : undefined}
                full
              >
                <select className="input-sharp" value={editing.dealer_id || ""} onChange={(e) => setEditing({ ...editing, dealer_id: e.target.value })} data-testid="store-field-dealer">
                  <option value="">— Select —</option>
                  {dealers.map((d) => {
                    const a = agents.find((x) => x.id === d.master_agent_id);
                    return <option key={d.id} value={d.id}>{d.name}{a ? ` — ${a.name}` : ""}</option>;
                  })}
                </select>
                {dealers.length === 0 && <p className="text-[11px] text-slate-500 mt-1 italic">No dealers defined. Create one under the Dealers tab first.</p>}
              </Field>
              <Toggle label="Active" v={editing.active} onChange={(v) => setEditing({ ...editing, active: v })} />
            </div>
          )}
          <DialogFooter>
            <button onClick={() => setOpen(false)} className="btn-outline-navy text-sm !py-2.5">Cancel</button>
            <button onClick={onSave} disabled={busy || !canSave} className="btn-brand text-sm !py-2.5" data-testid="store-save">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} {editing?.id ? "Save" : "Create"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </SectionShell>
  );
}



/* ---------------- Users & Security Roles ---------------- */
function UsersAndRolesSection() {
  const can = useCan();
  const canViewRoles = can("security_roles", "view");
  // Default to "users" tab; fall back to "roles" if the user can't see users.
  const initialTab = can("users", "view") ? "users" : (canViewRoles ? "roles" : "users");
  const [tab, setTab] = useState(initialTab);
  return (
    <div className="mt-6 space-y-4" data-testid="users-section">
      <div className="flex gap-2" data-testid="users-subtabs">
        {can("users", "view") && (
          <button
            className={`text-sm font-semibold px-5 py-2.5 border-2 transition ${tab === "users" ? "border-[#0B132B] bg-[#0B132B] text-white" : "border-slate-200 text-slate-600 hover:border-[#0B132B]"}`}
            onClick={() => setTab("users")}
            data-testid="users-tab-users"
          >
            Users
          </button>
        )}
        {canViewRoles && (
          <button
            className={`text-sm font-semibold px-5 py-2.5 border-2 transition ${tab === "roles" ? "border-[#0B132B] bg-[#0B132B] text-white" : "border-slate-200 text-slate-600 hover:border-[#0B132B]"}`}
            onClick={() => setTab("roles")}
            data-testid="users-tab-roles"
          >
            Security roles
          </button>
        )}
      </div>
      {tab === "users" ? <UsersListSection /> : <RolesListSection />}
    </div>
  );
}

function UsersListSection() {
  const { user } = useAuth();
  const can = useCan();
  const [items, setItems] = useState([]); const [roles, setRoles] = useState([]);
  const [agents, setAgents] = useState([]); const [dealers, setDealers] = useState([]); const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false); const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);
  // `editing.id` set = edit; null id = create
  const blankDraft = { id: null, name: "", email: "", username: "", password: "", role_id: "", master_agent_id: "", dealer_id: "", store_id: "", status: "active", security_answers: [] };

  const load = async () => {
    setLoading(true);
    // Each fetch is independently fault-tolerant — if the caller's role lacks `users:view`
    // or `security_roles:view` etc., that endpoint returns 403; we catch and treat as empty
    // so the rest of the dialog/table still renders without crashing the whole tab.
    const safe = async (path) => {
      try { return (await api.get(path)).data || []; }
      catch (e) { if (e.response?.status === 403) return []; throw e; }
    };
    try {
      const [u, r, a, d, s] = await Promise.all([
        safe("/admins"),
        safe("/roles"),
        safe("/master-agents"),
        safe("/dealers"),
        safe("/stores"),
      ]);
      setItems(u); setRoles(r); setAgents(a); setDealers(d); setStores(s);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const selectedRole = useMemo(() => roles.find((r) => r.id === editing?.role_id), [editing?.role_id, roles]);
  const tier = selectedRole?.tier || "none";
  const needsMa = ["master_agent", "management", "store_manager", "sales_rep"].includes(tier);
  const needsDealer = ["management", "store_manager", "sales_rep"].includes(tier);
  const needsStore = ["store_manager", "sales_rep"].includes(tier);

  // Filter dealers/stores by selected parent so the dropdowns chain cleanly.
  const filteredDealers = useMemo(
    () => (editing?.master_agent_id ? dealers.filter((d) => d.master_agent_id === editing.master_agent_id) : dealers),
    [editing?.master_agent_id, dealers]
  );
  const filteredStores = useMemo(
    () => (editing?.dealer_id ? stores.filter((s) => s.dealer_id === editing.dealer_id) : stores),
    [editing?.dealer_id, stores]
  );

  const onSave = async () => {
    setBusy(true);
    try {
      const scopeBody = {
        master_agent_id: needsMa ? (editing.master_agent_id || null) : null,
        dealer_id: needsDealer ? (editing.dealer_id || null) : null,
        store_id: needsStore ? (editing.store_id || null) : null,
      };
      if (editing.id) {
        // PUT — only send changed fields; backend handles partial updates.
        const body = {
          name: editing.name,
          email: editing.email,
          username: editing.username,
          role_id: editing.role_id || "",
          status: editing.status || "active",
          ...scopeBody,
        };
        if (editing.password) body.password = editing.password;
        await api.put(`/admins/${editing.id}`, body);
        toast.success("User updated");
      } else {
        const body = {
          name: editing.name,
          email: editing.email,
          username: editing.username,
          password: editing.password,
          role_id: editing.role_id || null,
          ...scopeBody,
        };
        await api.post("/admins", body);
        toast.success("User created");
      }
      setOpen(false); setEditing(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Remove this user?")) return;
    try { await api.delete(`/admins/${id}`); toast.success("Removed"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const roleName = (rid) => roles.find((r) => r.id === rid)?.name || "—";
  const isImmutable = (a) => a.is_super_admin;

  const canSave =
    editing &&
    editing.name && editing.email && editing.username &&
    (editing.id || editing.password) &&
    (!needsMa || editing.master_agent_id) &&
    (!needsDealer || editing.dealer_id) &&
    (!needsStore || editing.store_id);

  return (
    <SectionShell
      title="Users"
      onAdd={can("users", "manage") ? () => { setEditing({ ...blankDraft }); setOpen(true); } : undefined}
      addTestId="add-user"
    >
      {loading ? <Loading /> : items.length === 0 ? <Empty label="No users yet." /> : (
        <Table headers={["Full name", "Username", "Email", "Role", "Scope", "Created", ""]}>
          {items.map((a) => {
            const ma = agents.find((x) => x.id === a.master_agent_id);
            const dl = dealers.find((x) => x.id === a.dealer_id);
            const st = stores.find((x) => x.id === a.store_id);
            const scopeParts = [ma?.name, dl?.name, st?.name].filter(Boolean);
            return (
              <tr key={a.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`user-row-${a.id}`}>
                <Td>
                  <span className="font-semibold text-[#0B132B]">{a.name}</span>
                  {user?.id === a.id && <span className="ml-2 badge-pill badge-teal">you</span>}
                  {a.is_super_admin && <span className="ml-2 inline-block px-1.5 py-0.5 text-[9px] font-bold tracking-widest uppercase bg-[#00E5FF] text-[#0B132B]">Super</span>}
                  {a.status === "pending" && (
                    <span className="ml-2 inline-block px-1.5 py-0.5 text-[9px] font-bold tracking-widest uppercase bg-[#FFB703] text-[#0B132B]" data-testid={`user-pending-badge-${a.id}`}>Pending</span>
                  )}
                </Td>
                <Td className="mono">{a.username || <span className="text-slate-300">—</span>}</Td>
                <Td className="mono text-xs">{a.email}</Td>
                <Td className="text-xs">
                  {a.is_super_admin
                    ? <span className="text-slate-400 italic">— all access —</span>
                    : (a.role_id ? roleName(a.role_id) : <span className="text-slate-300">No role</span>)}
                </Td>
                <Td className="text-xs text-slate-600">
                  {scopeParts.length > 0 ? scopeParts.join(" › ") : <span className="text-slate-300">—</span>}
                </Td>
                <Td className="mono text-xs text-slate-500">{new Date(a.created_at).toLocaleDateString()}</Td>
                <Td className="text-right">
                  <div className="inline-flex gap-2">
                    {can("users", "manage") && !isImmutable(a) && (
                      <button onClick={() => { setEditing({ id: a.id, name: a.name, email: a.email, username: a.username || "", password: "", role_id: a.role_id || "", master_agent_id: a.master_agent_id || "", dealer_id: a.dealer_id || "", store_id: a.store_id || "", status: a.status || "active", security_answers: a.security_answers || [] }); setOpen(true); }} className="p-2 hover:bg-slate-100" data-testid={`user-edit-${a.id}`}>
                        <Pencil className="h-4 w-4 text-slate-600" />
                      </button>
                    )}
                    {can("users", "delete") && !isImmutable(a) && user?.id !== a.id && (
                      <button onClick={() => onDelete(a.id)} className="p-2 hover:bg-red-50" data-testid={`user-delete-${a.id}`}>
                        <Trash2 className="h-4 w-4 text-red-600" />
                      </button>
                    )}
                  </div>
                </Td>
              </tr>
            );
          })}
        </Table>
      )}

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditing(null); }}>
        <DialogContent className="rounded-none max-w-2xl" data-testid="user-dialog">
          <DialogHeader><DialogTitle>{editing?.id ? "Edit user" : "New user"}</DialogTitle></DialogHeader>
          {editing && (() => {
            // Show the "Security questions" tab only when this user actually went through
            // the public self-service registration (has answers stored). Admin-created users
            // skip those questions, so tab 2 is meaningless for them — hide it entirely.
            const hasSecurityAnswers = Array.isArray(editing.security_answers) && editing.security_answers.length > 0;
            return (
              <Tabs defaultValue="general" data-testid="user-dialog-tabs">
                <TabsList className={`grid w-full ${hasSecurityAnswers ? "grid-cols-2" : "grid-cols-1"}`}>
                  <TabsTrigger value="general" data-testid="user-tab-general">General</TabsTrigger>
                  {hasSecurityAnswers && (
                    <TabsTrigger value="security" data-testid="user-tab-security">
                      Security questions
                    </TabsTrigger>
                  )}
                </TabsList>

                <TabsContent value="general" className="mt-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {/* Status — admins approve pending self-registered users by flipping
                        this to Active. Hidden on the create form since admin-created users
                        are always active. */}
                    {editing.id && (
                      <Field label="Status" info="Pending users cannot sign in. Approve a self-registered user by switching to Active." full>
                        <select
                          className="input-sharp"
                          value={editing.status || "active"}
                          onChange={(e) => setEditing({ ...editing, status: e.target.value })}
                          data-testid="user-field-status"
                        >
                          <option value="pending">Pending</option>
                          <option value="active">Active</option>
                        </select>
                      </Field>
                    )}
                    <Field label="Full name" full>
                      <input className="input-sharp" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="user-field-fullname" />
                    </Field>
                    <Field
                      label="Email"
                      info="Used for password reset notifications."
                      full
                    >
                      <input type="email" className="input-sharp" value={editing.email} onChange={(e) => setEditing({ ...editing, email: e.target.value })} data-testid="user-field-email" />
                    </Field>
                    <Field
                      label="Username"
                      info="Used to log in. Must be a single word, no spaces."
                    >
                      <input
                        className="input-sharp"
                        value={editing.username}
                        onChange={(e) => setEditing({ ...editing, username: e.target.value.toLowerCase().replace(/\s+/g, "") })}
                        data-testid="user-field-username"
                      />
                    </Field>
                    <Field label={editing.id ? "New password (optional)" : "Password"}>
                      <input type="password" className="input-sharp" value={editing.password} onChange={(e) => setEditing({ ...editing, password: e.target.value })} data-testid="user-field-password" />
                    </Field>
                    <Field
                      label="Security role"
                      info="The list of available roles is filtered by your tier — you can only assign roles at or below your own."
                      full
                    >
                      <select
                        className="input-sharp"
                        value={editing.role_id || ""}
                        onChange={(e) => setEditing({ ...editing, role_id: e.target.value, master_agent_id: "", dealer_id: "", store_id: "" })}
                        data-testid="user-field-role"
                      >
                        <option value="">— No role —</option>
                        {roles.map((r) => <option key={r.id} value={r.id}>{r.name}{r.tier && r.tier !== "none" ? ` · ${r.tier.replace("_", " ")}` : ""}</option>)}
                      </select>
                      {roles.length === 0 && <p className="text-[11px] text-slate-500 mt-1 italic">No roles defined yet. Create one under the Security roles tab.</p>}
                    </Field>

                    {/* Scope dropdowns — appear based on the selected role's tier. */}
                    {needsMa && (
                      <Field label="Master agent" full>
                        <select
                          className="input-sharp"
                          value={editing.master_agent_id || ""}
                          onChange={(e) => setEditing({ ...editing, master_agent_id: e.target.value, dealer_id: "", store_id: "" })}
                          data-testid="user-field-master-agent"
                        >
                          <option value="">— Select master agent —</option>
                          {agents.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                        </select>
                        {agents.length === 0 && <p className="text-[11px] text-slate-500 mt-1 italic">No master agents available in your scope.</p>}
                      </Field>
                    )}
                    {needsDealer && (
                      <Field label="Dealer">
                        <select
                          className="input-sharp"
                          value={editing.dealer_id || ""}
                          disabled={!editing.master_agent_id}
                          onChange={(e) => setEditing({ ...editing, dealer_id: e.target.value, store_id: "" })}
                          data-testid="user-field-dealer"
                        >
                          <option value="">— Select dealer —</option>
                          {filteredDealers.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                        </select>
                      </Field>
                    )}
                    {needsStore && (
                      <Field label="Store">
                        <select
                          className="input-sharp"
                          value={editing.store_id || ""}
                          disabled={!editing.dealer_id}
                          onChange={(e) => setEditing({ ...editing, store_id: e.target.value })}
                          data-testid="user-field-store"
                        >
                          <option value="">— Select store —</option>
                          {filteredStores.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                        </select>
                      </Field>
                    )}
                  </div>
                </TabsContent>

                {hasSecurityAnswers && (
                  <TabsContent value="security" className="mt-4" data-testid="user-security-tab-content">
                    <p className="text-xs text-slate-500 mb-4">
                      These are the answers <strong>{editing.name}</strong> provided when registering. Review them, and if they look correct switch the Status on the General tab to <strong>Active</strong>.
                    </p>
                    <div className="space-y-4">
                      {editing.security_answers.map((qa, i) => (
                        <div key={i} className="border-l-2 border-[#00E5FF] pl-3" data-testid={`user-security-answer-${i + 1}`}>
                          <div className="tw-overline">Q{i + 1}</div>
                          <div className="mt-0.5 text-sm font-semibold text-[#0B132B]">{qa.question}</div>
                          <div className="mt-1 text-sm text-slate-700 italic">"{qa.answer}"</div>
                        </div>
                      ))}
                    </div>
                  </TabsContent>
                )}
              </Tabs>
            );
          })()}
          <DialogFooter>
            <button onClick={() => setOpen(false)} className="btn-outline-navy text-sm !py-2.5">Cancel</button>
            <button
              onClick={onSave}
              disabled={busy || !canSave}
              className="btn-brand text-sm !py-2.5"
              data-testid="user-save"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} {editing?.id ? "Save" : "Create"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </SectionShell>
  );
}

function RolesListSection() {
  const can = useCan();
  const canManage = can("security_roles", "manage");
  const canDelete = can("security_roles", "delete");
  const [items, setItems] = useState([]);
  const [resources, setResources] = useState({});  // {resource: [levels]}
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false); const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);  // {id?, name, description, permissions}

  const load = async () => {
    setLoading(true);
    try {
      const [r, p] = await Promise.all([api.get("/roles"), api.get("/roles/permission-resources")]);
      setItems(r.data || []); setResources(p.data || {});
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const blankPerms = useMemo(() => {
    const out = {};
    for (const k of Object.keys(resources)) out[k] = "no_access";
    return out;
  }, [resources]);

  const onSave = async () => {
    setBusy(true);
    try {
      const body = { name: editing.name, description: editing.description || "", tier: editing.tier || "none", permissions: editing.permissions };
      if (editing.id) await api.put(`/roles/${editing.id}`, body);
      else await api.post("/roles", body);
      toast.success("Role saved");
      setOpen(false); setEditing(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this role? Users assigned to it will lose their permissions.")) return;
    try { await api.delete(`/roles/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const setLevel = (resource, level) => {
    setEditing((prev) => ({ ...prev, permissions: { ...prev.permissions, [resource]: level } }));
  };

  // Compact summary chip on the table row
  const summarise = (perms) => {
    const counts = { delete: 0, manage: 0, view: 0, no_access: 0 };
    for (const k of Object.keys(resources)) counts[perms?.[k] || "no_access"] += 1;
    return counts;
  };

  return (
    <SectionShell
      title="Security roles"
      onAdd={canManage ? () => { setEditing({ id: null, name: "", description: "", tier: "none", permissions: { ...blankPerms } }); setOpen(true); } : undefined}
      addTestId="add-role"
    >
      {loading ? <Loading /> : items.length === 0 ? <Empty label="No security roles yet. Click + New to add one." /> : (
        <Table headers={["Name", "Tier", "Description", "Summary", ""]}>
          {items.map((r) => {
            const s = summarise(r.permissions);
            return (
              <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`role-row-${r.id}`}>
                <Td><span className="font-semibold text-[#0B132B]">{r.name}</span></Td>
                <Td className="text-[10px] mono uppercase tracking-widest text-[#008B99]">{(r.tier || "none").replace("_", " ")}</Td>
                <Td className="text-xs text-slate-600">{r.description || <span className="text-slate-300">—</span>}</Td>
                <Td className="text-[10px] mono uppercase tracking-widest">
                  <span className="mr-2 text-red-600">{s.delete} delete</span>
                  <span className="mr-2 text-[#008B99]">{s.manage} manage</span>
                  <span className="mr-2 text-slate-700">{s.view} view</span>
                  <span className="text-slate-400">{s.no_access} no acc.</span>
                </Td>
                <Td className="text-right">
                  <div className="inline-flex gap-2">
                    {canManage && (
                      <button onClick={() => { setEditing({ id: r.id, name: r.name, description: r.description || "", tier: r.tier || "none", permissions: { ...blankPerms, ...r.permissions } }); setOpen(true); }} className="p-2 hover:bg-slate-100" data-testid={`role-edit-${r.id}`}>
                        <Pencil className="h-4 w-4 text-slate-600" />
                      </button>
                    )}
                    {canDelete && (
                      <button onClick={() => onDelete(r.id)} className="p-2 hover:bg-red-50" data-testid={`role-delete-${r.id}`}>
                        <Trash2 className="h-4 w-4 text-red-600" />
                      </button>
                    )}
                  </div>
                </Td>
              </tr>
            );
          })}
        </Table>
      )}

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditing(null); }}>
        <DialogContent className="rounded-none max-w-3xl" data-testid="role-dialog">
          <DialogHeader><DialogTitle>{editing?.id ? "Edit role" : "New role"}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <Field label="Name">
                  <input className="input-sharp" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="role-field-name" />
                </Field>
                <Field
                  label="Scope tier"
                  info="Drives which Master Agent / Dealer / Store data users with this role can see. 'None' = unscoped (only globally-public data)."
                >
                  <select
                    className="input-sharp"
                    value={editing.tier || "none"}
                    onChange={(e) => setEditing({ ...editing, tier: e.target.value })}
                    data-testid="role-field-tier"
                  >
                    <option value="none">None — unscoped</option>
                    <option value="total_wireless">Total Wireless</option>
                    <option value="master_agent">Master Agent</option>
                    <option value="management">Management (Dealer)</option>
                    <option value="store_manager">Store Manager</option>
                    <option value="sales_rep">Sales Rep</option>
                  </select>
                </Field>
                <Field label="Description">
                  <input className="input-sharp" value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} data-testid="role-field-description" />
                </Field>
              </div>
              <div className="bg-slate-50 border border-slate-200 p-4">
                <div className="tw-overline mb-3 flex items-center gap-2">
                  Permissions
                  <span
                    className="inline-flex items-center justify-center h-4 w-4 text-slate-400 hover:text-[#0B132B] cursor-help"
                    title={"No access — section is hidden.\nView — can view, but not edit or delete.\nManage — can view and edit, but not delete.\nDelete — can view, edit, and delete."}
                    data-testid="role-perm-legend"
                  >
                    <Info className="h-3.5 w-3.5" />
                  </span>
                </div>
                <div className="space-y-1">
                  {Object.entries(resources).map(([resource, levels]) => (
                    <div key={resource} className="grid grid-cols-[140px_1fr] items-center gap-2 py-1.5 border-b border-slate-100 last:border-0" data-testid={`role-perm-row-${resource}`}>
                      <span className="text-sm font-semibold text-[#0B132B] capitalize">{resource.replace("_", " ")}</span>
                      <div className="flex flex-wrap gap-1">
                        {levels.map((lvl) => {
                          const active = (editing.permissions?.[resource] || "no_access") === lvl;
                          return (
                            <button
                              key={lvl}
                              type="button"
                              onClick={() => setLevel(resource, lvl)}
                              className={`text-[10px] mono uppercase tracking-widest px-2.5 py-1 border transition ${active ? "bg-[#0B132B] text-white border-[#0B132B]" : "bg-white text-slate-600 border-slate-300 hover:border-[#0B132B]"}`}
                              data-testid={`role-perm-${resource}-${lvl}`}
                            >
                              {lvl.replace("_", " ")}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <button onClick={() => setOpen(false)} className="btn-outline-navy text-sm !py-2.5">Cancel</button>
            <button onClick={onSave} disabled={busy || !editing?.name} className="btn-brand text-sm !py-2.5" data-testid="role-save">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} {editing?.id ? "Save" : "Create"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </SectionShell>
  );
}

/* ---------------- Quotes (saved & drafts) ---------------- */
function QuotesSection() {
  const can = useCan();
  const canDelete = can("quotes", "delete");
  const [view, setView] = useState("saved"); // "saved" | "drafts"
  const [saved, setSaved] = useState([]); const [drafts, setDrafts] = useState([]);
  const [devices, setDevices] = useState({}); const [carriers, setCarriers] = useState({});
  const [agents, setAgents] = useState({}); const [dealers, setDealers] = useState({}); const [stores, setStores] = useState({});
  const [users, setUsers] = useState({});
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);

  // Search by quote number — calls the scoped lookup endpoint and routes to the public quote
  // viewer (which works for any role since GET /quotes/{id} is unauthenticated).
  const [search, setSearch] = useState("");
  const [searching, setSearching] = useState(false);
  const navigate = useNavigate();
  const onSearch = async (e) => {
    e?.preventDefault?.();
    const q = search.trim().toUpperCase();
    if (!q) return;
    setSearching(true);
    try {
      const { data } = await api.get(`/quotes/by-number/${encodeURIComponent(q)}`);
      navigate(`/quote/${data.id}`);
    } catch (err) {
      toast.error(err.response?.status === 404 ? "Quote not found in your scope" : formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSearching(false); }
  };

  // Per-row delete. `kind` is "saved" (Quote) or "drafts" (DeviceQuote).
  const deleteOne = async (kind, id) => {
    const label = kind === "saved" ? "saved quote" : "draft";
    if (!window.confirm(`Delete this ${label}? This cannot be undone.`)) return;
    setDeletingId(id);
    try {
      const path = kind === "saved" ? `/quotes/${id}` : `/device-quotes/${id}`;
      await api.delete(path);
      if (kind === "saved") setSaved((s) => s.filter((q) => q.id !== id));
      else setDrafts((s) => s.filter((q) => q.id !== id));
      toast.success(`${label[0].toUpperCase()}${label.slice(1)} deleted`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setDeletingId(null); }
  };

  // Bulk delete-all (current view only). Scoped server-side to caller's tier.
  const deleteAll = async () => {
    const count = view === "saved" ? saved.length : drafts.length;
    if (count === 0) return;
    const label = view === "saved" ? "saved quotes" : "drafts";
    if (!window.confirm(`Delete all ${count} ${label} in your scope? This cannot be undone.`)) return;
    setBulkBusy(true);
    try {
      const path = view === "saved" ? "/quotes" : "/device-quotes";
      const { data } = await api.delete(path);
      if (view === "saved") setSaved([]); else setDrafts([]);
      toast.success(`Deleted ${data?.deleted ?? count} ${label}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBulkBusy(false); }
  };

  useEffect(() => {
    const safe = async (path) => {
      try { return (await api.get(path)).data || []; }
      catch (e) { if (e.response?.status === 403) return []; throw e; }
    };
    (async () => {
      try {
        const [s, d, dv, cr, ma, dl, st, us] = await Promise.all([
          safe("/quotes"),
          safe("/device-quotes"),
          safe("/devices"),
          safe("/carriers"),
          safe("/master-agents"),
          safe("/dealers"),
          safe("/stores"),
          safe("/admins"),
        ]);
        setSaved(s); setDrafts(d);
        setDevices(Object.fromEntries(dv.map((x) => [x.id, x])));
        setCarriers(Object.fromEntries(cr.map((x) => [x.id, x])));
        setAgents(Object.fromEntries(ma.map((x) => [x.id, x.name])));
        setDealers(Object.fromEntries(dl.map((x) => [x.id, x.name])));
        setStores(Object.fromEntries(st.map((x) => [x.id, x.name])));
        setUsers(Object.fromEntries(us.map((x) => [x.id, x.name])));
      } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
      finally { setLoading(false); }
    })();
  }, []);

  // Quote attribution breadcrumb — Master Agent › Dealer › Store, with rep name underneath.
  const scopeChip = (q) => {
    const parts = [agents[q.master_agent_id], dealers[q.dealer_id], stores[q.store_id]].filter(Boolean);
    return (
      <div>
        <div className="text-[11px] text-slate-700">{parts.length > 0 ? parts.join(" › ") : <span className="text-slate-300">— public —</span>}</div>
        {q.created_by_user_id && <div className="text-[10px] mono text-[#008B99]">by {users[q.created_by_user_id] || "user"}</div>}
      </div>
    );
  };

  return (
    <div className="mt-6 space-y-4" data-testid="quotes-section">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex gap-2" data-testid="quotes-subtabs">
          <button
            className={`text-sm font-semibold px-5 py-2.5 border-2 transition ${view === "saved" ? "border-[#0B132B] bg-[#0B132B] text-white" : "border-slate-200 text-slate-600 hover:border-[#0B132B]"}`}
            onClick={() => setView("saved")}
            data-testid="quotes-tab-saved"
          >
            Saved quotes <span className="mono text-xs ml-2">({saved.length})</span>
          </button>
          <button
            className={`text-sm font-semibold px-5 py-2.5 border-2 transition ${view === "drafts" ? "border-[#0B132B] bg-[#0B132B] text-white" : "border-slate-200 text-slate-600 hover:border-[#0B132B]"}`}
            onClick={() => setView("drafts")}
            data-testid="quotes-tab-drafts"
          >
            Drafts (deal builder) <span className="mono text-xs ml-2">({drafts.length})</span>
          </button>
        </div>
        <form onSubmit={onSearch} className="flex items-center gap-2 border border-slate-300 bg-white px-3 py-2 focus-within:border-[#0B132B] min-w-[260px]" data-testid="quotes-search-form">
          <FileSpreadsheet className="h-4 w-4 text-slate-400" />
          <input
            type="text"
            placeholder="Look up quote # (e.g. Q-00042)"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="bg-transparent outline-none text-sm flex-1 mono uppercase"
            data-testid="quotes-search-input"
          />
          <button type="submit" disabled={searching || !search.trim()} className="text-xs font-bold tracking-widest uppercase text-[#0B132B] hover:text-[#008B99] disabled:opacity-40" data-testid="quotes-search-submit">
            {searching ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Go"}
          </button>
        </form>
        {canDelete && (
          <button
            type="button"
            onClick={deleteAll}
            disabled={bulkBusy || (view === "saved" ? saved.length === 0 : drafts.length === 0)}
            className="inline-flex items-center gap-2 text-xs font-bold tracking-widest uppercase px-3 py-2 border-2 border-red-300 text-red-600 hover:bg-red-50 disabled:opacity-40"
            data-testid="quotes-delete-all"
          >
            {bulkBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
            Delete all
          </button>
        )}
      </div>

      <div className="bg-white border border-slate-200">
        {loading ? <Loading /> : view === "saved" ? (
          saved.length === 0 ? <Empty label="No saved quotes yet." /> : (
            <Table headers={["Quote #", "When", "Plan", "Lines", "Device", "Total", "Attribution", ""]}>
              {saved.map((q) => (
                <tr key={q.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`saved-quote-${q.id}`}>
                  <Td className="mono text-xs font-semibold text-[#0B132B]">{q.quote_number || <span className="text-slate-300">—</span>}</Td>
                  <Td className="mono text-xs text-slate-500">{new Date(q.created_at).toLocaleString()}</Td>
                  <Td><span className="font-semibold text-[#0B132B]">{q.plan_name}</span></Td>
                  <Td className="mono">{q.lines}</Td>
                  <Td className="text-xs text-slate-600">{q.device_name || <span className="text-slate-300">—</span>}</Td>
                  <Td className="mono">${(q.total || 0).toFixed(2)}</Td>
                  <Td>{scopeChip(q)}</Td>
                  <Td className="text-right">
                    <div className="inline-flex items-center gap-3">
                      <Link to={`/quote/${q.id}`} target="_blank" className="text-xs text-[#008B99] hover:text-[#0B132B] underline">open</Link>
                      {canDelete && (
                        <button
                          type="button"
                          onClick={() => deleteOne("saved", q.id)}
                          disabled={deletingId === q.id}
                          className="p-1 hover:bg-red-50 disabled:opacity-40"
                          data-testid={`saved-quote-delete-${q.id}`}
                          aria-label="Delete quote"
                          title="Delete quote"
                        >
                          {deletingId === q.id
                            ? <Loader2 className="h-4 w-4 animate-spin text-red-600" />
                            : <Trash2 className="h-4 w-4 text-red-600" />}
                        </button>
                      )}
                    </div>
                  </Td>
                </tr>
              ))}
            </Table>
          )
        ) : (
          drafts.length === 0 ? <Empty label="No deal-builder drafts yet." /> : (
            <Table headers={["When", "Device", "Recommended", "Price", "Porting?", "Carrier", "ID?", "Views", ""]}>
              {drafts.map((d) => (
                <tr key={d.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`draft-quote-${d.id}`}>
                  <Td className="mono text-xs text-slate-500">{new Date(d.created_at).toLocaleString()}</Td>
                  <Td>
                    <span className="font-semibold text-[#0B132B]">
                      {devices[d.device_id] ? `${devices[d.device_id].brand} ${devices[d.device_id].model}` : "—"}
                    </span>
                  </Td>
                  <Td>{d.recommended_key ? <span className="badge-pill badge-teal">{d.recommended_key}</span> : <span className="text-slate-300">—</span>}</Td>
                  <Td className="mono">{d.recommended_price != null ? `$${d.recommended_price.toFixed(2)}` : "—"}</Td>
                  <Td className="mono text-xs">{d.porting === true ? "Yes" : d.porting === false ? "No" : "—"}</Td>
                  <Td className="text-xs">{carriers[d.from_carrier_id]?.name || <span className="text-slate-300">—</span>}</Td>
                  <Td className="mono text-xs">{d.has_id === true ? "Yes" : d.has_id === false ? "No" : "—"}</Td>
                  <Td className="mono inline-flex items-center gap-1"><Eye className="h-3 w-3 text-slate-400" />{d.views || 0}</Td>
                  <Td className="text-right">
                    <div className="inline-flex items-center gap-3">
                      <Link to={`/devices/${d.device_id}?dq=${d.id}`} target="_blank" className="text-xs text-[#008B99] hover:text-[#0B132B] underline">open</Link>
                      {canDelete && (
                        <button
                          type="button"
                          onClick={() => deleteOne("drafts", d.id)}
                          disabled={deletingId === d.id}
                          className="p-1 hover:bg-red-50 disabled:opacity-40"
                          data-testid={`draft-quote-delete-${d.id}`}
                          aria-label="Delete draft"
                          title="Delete draft"
                        >
                          {deletingId === d.id
                            ? <Loader2 className="h-4 w-4 animate-spin text-red-600" />
                            : <Trash2 className="h-4 w-4 text-red-600" />}
                        </button>
                      )}
                    </div>
                  </Td>
                </tr>
              ))}
            </Table>
          )
        )}
      </div>
    </div>
  );
}

const SUGGESTION_CATEGORIES = [
  { value: "bug", label: "Bug" },
  { value: "feature", label: "Feature" },
  { value: "improvement", label: "Improvement" },
  { value: "other", label: "Other" },
];
const SUGGESTION_STATUSES = [
  { value: "open", label: "Open", className: "badge-pill badge-outline" },
  { value: "in_review", label: "In review", className: "badge-pill badge-amber" },
  { value: "in_progress", label: "In progress", className: "badge-pill badge-teal" },
  { value: "done", label: "Done", className: "badge-pill bg-emerald-100 text-emerald-700 border border-emerald-200" },
  { value: "wontfix", label: "Won't fix", className: "badge-pill bg-slate-100 text-slate-500 border border-slate-200" },
];

function SuggestionsSection() {
  const can = useCan();
  const { user } = useAuth();
  const canManage = can("suggestions", "manage");
  const canDelete = can("suggestions", "delete");
  const canList = can("suggestions", "view");
  const currentUserId = user?.id;

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", category: "feature" });
  const [busyId, setBusyId] = useState(null);
  const [voteBusyId, setVoteBusyId] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  // View modal — opens when admin clicks "View" on a row. Carries the suggestion id; the live
  // doc is read from `items` so any in-flight upvote/status edits stay in sync.
  const [viewId, setViewId] = useState(null);
  const [responseDraft, setResponseDraft] = useState("");
  const [responseBusy, setResponseBusy] = useState(false);
  const viewing = viewId ? items.find((s) => s.id === viewId) : null;
  useEffect(() => { setResponseDraft(viewing?.admin_note || ""); }, [viewId, viewing?.admin_note]);

  const saveResponse = async () => {
    if (!viewing) return;
    setResponseBusy(true);
    try {
      const { data } = await api.patch(`/suggestions/${viewing.id}`, { admin_note: responseDraft });
      setItems((cur) => cur.map((s) => (s.id === data.id ? data : s)));
      toast.success("Response saved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setResponseBusy(false); }
  };

  const load = async () => {
    if (!canList) { setLoading(false); return; }
    setLoading(true);
    try {
      const { data } = await api.get("/suggestions");
      setItems(data || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!form.title.trim()) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/suggestions", {
        title: form.title.trim(),
        description: form.description.trim(),
        category: form.category,
      });
      setForm({ title: "", description: "", category: "feature" });
      // When the backend dedupes, it returns the EXISTING suggestion id with the new submitter
      // auto-upvoted in. Replace the row in place; otherwise prepend the brand-new doc.
      if (canList) {
        setItems((cur) => {
          const existed = cur.find((s) => s.id === data.id);
          return existed ? cur.map((s) => (s.id === data.id ? data : s)) : [data, ...cur];
        });
      }
      // Detect the dedupe path: voter count >= 2 OR a brand new merged_note entry tells us
      // the server treated this as a duplicate. Tailor the toast accordingly.
      const wasMerged = (data.upvote_count || 0) > 1 || (data.merged_notes || []).some((n) => n.text === form.description.trim());
      toast.success(wasMerged
        ? "Same idea already exists — your vote was added to the original."
        : "Suggestion submitted — thanks!");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setSubmitting(false); }
  };

  const updateStatus = async (id, status) => {
    setBusyId(id);
    try {
      const { data } = await api.patch(`/suggestions/${id}`, { status });
      setItems((cur) => cur.map((s) => (s.id === id ? data : s)));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusyId(null); }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this suggestion? This cannot be undone.")) return;
    setBusyId(id);
    try {
      await api.delete(`/suggestions/${id}`);
      setItems((cur) => cur.filter((s) => s.id !== id));
      toast.success("Suggestion deleted");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusyId(null); }
  };

  const toggleUpvote = async (id) => {
    setVoteBusyId(id);
    try {
      const { data } = await api.post(`/suggestions/${id}/upvote`);
      setItems((cur) => cur.map((s) => (s.id === id ? data : s)));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setVoteBusyId(null); }
  };

  const filtered = useMemo(() => {
    const matches = items.filter((s) => {
      if (statusFilter && s.status !== statusFilter) return false;
      if (categoryFilter && s.category !== categoryFilter) return false;
      return true;
    });
    // Highest upvote_count first; tie-break by newest. Helps admins triage the loudest signals.
    return matches.sort((a, b) => {
      const ua = a.upvote_count || 0;
      const ub = b.upvote_count || 0;
      if (ub !== ua) return ub - ua;
      return new Date(b.created_at) - new Date(a.created_at);
    });
  }, [items, statusFilter, categoryFilter]);

  const statusMeta = (v) => SUGGESTION_STATUSES.find((s) => s.value === v) || SUGGESTION_STATUSES[0];
  const categoryLabel = (v) => (SUGGESTION_CATEGORIES.find((c) => c.value === v) || {}).label || v;

  return (
    <div className="mt-6 space-y-6" data-testid="suggestions-section">
      <div className="bg-white border border-slate-200 p-6">
        <div className="flex items-center gap-2 mb-1">
          <Lightbulb className="h-5 w-5 text-[#00E5FF]" />
          <h2 className="text-xl font-bold text-[#0B132B]">Submit a suggestion</h2>
        </div>
        <p className="text-sm text-slate-500 mb-4">
          Spot a bug or have an idea? Send it to the admins below — every submission is attributed to your user.
        </p>
        <form onSubmit={submit} className="space-y-3" data-testid="suggestion-form">
          <div className="grid grid-cols-1 md:grid-cols-[1fr_220px] gap-3">
            <input
              className="input-sharp"
              placeholder="Short title (e.g. Bundle totals are stale)"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              maxLength={140}
              required
              data-testid="suggestion-title"
            />
            <select
              className="input-sharp"
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
              data-testid="suggestion-category"
            >
              {SUGGESTION_CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </div>
          <textarea
            className="input-sharp min-h-[100px]"
            placeholder="Optional details — steps to reproduce a bug, the use case for a feature, etc."
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            maxLength={4000}
            data-testid="suggestion-description"
          />
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={submitting || !form.title.trim()}
              className="btn-brand disabled:opacity-40"
              data-testid="suggestion-submit"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Submit
            </button>
          </div>
        </form>
      </div>

      {canList && (
        <div className="bg-white border border-slate-200">
          <div className="flex flex-wrap items-center justify-between gap-3 p-5 border-b border-slate-200">
            <h2 className="text-xl font-bold text-[#0B132B]">All suggestions <span className="mono text-sm text-slate-400">({filtered.length})</span></h2>
            <div className="flex gap-2">
              <select
                className="input-sharp !py-2 !px-3 text-xs"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                data-testid="suggestion-filter-status"
              >
                <option value="">All statuses</option>
                {SUGGESTION_STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
              <select
                className="input-sharp !py-2 !px-3 text-xs"
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                data-testid="suggestion-filter-category"
              >
                <option value="">All types</option>
                {SUGGESTION_CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
          </div>

          {loading ? <Loading /> : filtered.length === 0 ? <Empty label="No suggestions yet." /> : (
            <Table headers={["Votes", "When", "Title", "Type", "Status", "From", "View", ""]}>
              {filtered.map((s) => {
                const m = statusMeta(s.status);
                const hasVoted = !!(currentUserId && (s.upvote_user_ids || []).includes(currentUserId));
                return (
                  <tr key={s.id} className="border-b border-slate-100 hover:bg-slate-50 align-top" data-testid={`suggestion-row-${s.id}`}>
                    <Td>
                      <button
                        type="button"
                        onClick={() => toggleUpvote(s.id)}
                        disabled={voteBusyId === s.id}
                        aria-pressed={hasVoted}
                        title={hasVoted ? "Remove your upvote" : "Upvote this suggestion"}
                        className={`inline-flex items-center gap-1.5 border-2 px-2 py-1 transition ${
                          hasVoted
                            ? "border-[#00E5FF] bg-[#00E5FF]/15 text-[#0B132B]"
                            : "border-slate-300 text-slate-600 hover:border-[#0B132B] hover:text-[#0B132B]"
                        } disabled:opacity-40`}
                        data-testid={`suggestion-upvote-${s.id}`}
                      >
                        {voteBusyId === s.id
                          ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          : <ThumbsUp className={`h-3.5 w-3.5 ${hasVoted ? "fill-current" : ""}`} />}
                        <span className="mono text-xs font-bold" data-testid={`suggestion-upvote-count-${s.id}`}>{s.upvote_count || 0}</span>
                      </button>
                    </Td>
                    <Td className="mono text-xs text-slate-500">{new Date(s.created_at).toLocaleString()}</Td>
                    <Td>
                      <div className="font-semibold text-[#0B132B]">{s.title}</div>
                      {s.description && <div className="text-xs text-slate-600 mt-1 whitespace-pre-wrap">{s.description}</div>}
                    </Td>
                    <Td><span className="badge-pill badge-outline">{categoryLabel(s.category)}</span></Td>
                    <Td>
                      {canManage ? (
                        <select
                          className="input-sharp !py-1.5 !px-2 text-xs"
                          value={s.status}
                          onChange={(e) => updateStatus(s.id, e.target.value)}
                          disabled={busyId === s.id}
                          data-testid={`suggestion-status-${s.id}`}
                        >
                          {SUGGESTION_STATUSES.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                        </select>
                      ) : (
                        <span className={m.className}>{m.label}</span>
                      )}
                    </Td>
                    <Td className="text-xs text-slate-600" data-testid={`suggestion-submitter-${s.id}`}>{s.submitter_label || <span className="text-slate-300">—</span>}</Td>
                    <Td>
                      <button
                        type="button"
                        onClick={() => setViewId(s.id)}
                        className="inline-flex items-center gap-1.5 border-2 border-slate-300 hover:border-[#0B132B] hover:text-[#0B132B] px-2 py-1 text-xs font-bold tracking-widest uppercase text-slate-600"
                        data-testid={`suggestion-view-${s.id}`}
                      >
                        <Eye className="h-3.5 w-3.5" /> View
                      </button>
                    </Td>
                    <Td className="text-right">
                      {canDelete && (
                        <button
                          type="button"
                          onClick={() => onDelete(s.id)}
                          disabled={busyId === s.id}
                          className="p-1 hover:bg-red-50 disabled:opacity-40"
                          data-testid={`suggestion-delete-${s.id}`}
                          aria-label="Delete suggestion"
                          title="Delete suggestion"
                        >
                          {busyId === s.id
                            ? <Loader2 className="h-4 w-4 animate-spin text-red-600" />
                            : <Trash2 className="h-4 w-4 text-red-600" />}
                        </button>
                      )}
                    </Td>
                  </tr>
                );
              })}
            </Table>
          )}
        </div>
      )}

      <Dialog open={!!viewId} onOpenChange={(v) => { if (!v) setViewId(null); }}>
        <DialogContent className="max-w-2xl rounded-none" data-testid="suggestion-view-dialog">
          {viewing && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-start gap-3 pr-6">
                  <Lightbulb className="h-5 w-5 text-[#00E5FF] mt-0.5 shrink-0" />
                  <span className="text-lg font-bold text-[#0B132B]">{viewing.title}</span>
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-5 pt-2">
                <div className="flex flex-wrap items-center gap-2 text-xs">
                  <span className="badge-pill badge-outline">{categoryLabel(viewing.category)}</span>
                  <span className={statusMeta(viewing.status).className}>{statusMeta(viewing.status).label}</span>
                  <span className="mono text-slate-500">From {viewing.submitter_label || "—"}</span>
                  <span className="mono text-slate-400">· {new Date(viewing.created_at).toLocaleString()}</span>
                </div>

                {viewing.description && (
                  <div>
                    <div className="tw-overline mb-1">Original description</div>
                    <p className="text-sm text-slate-700 whitespace-pre-wrap">{viewing.description}</p>
                  </div>
                )}

                <div>
                  <div className="tw-overline mb-1.5">Upvotes <span className="mono text-[#0B132B]">({viewing.upvote_count || 0})</span></div>
                  {(viewing.upvote_labels || []).length === 0 ? (
                    <p className="text-xs text-slate-400">No upvotes yet.</p>
                  ) : (
                    <div className="flex flex-wrap gap-1.5" data-testid="suggestion-view-voters">
                      {viewing.upvote_labels.map((label, i) => (
                        <span key={i} className="badge-pill bg-[#00E5FF]/15 border border-[#00E5FF]/40 text-[#0B132B] text-[11px]">
                          <ThumbsUp className="h-3 w-3 fill-current" /> {label}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {(viewing.merged_notes || []).length > 0 && (
                  <div>
                    <div className="tw-overline mb-1.5">Other submissions for this same idea</div>
                    <ul className="space-y-2" data-testid="suggestion-view-merged-notes">
                      {viewing.merged_notes.map((n, i) => (
                        <li key={i} className="border-l-2 border-[#00E5FF] pl-3 py-1">
                          <div className="text-[11px] mono uppercase tracking-widest text-slate-500">
                            {n.from} <span className="text-slate-400">· {new Date(n.at).toLocaleString()}</span>
                          </div>
                          <p className="text-sm text-slate-700 whitespace-pre-wrap">{n.text}</p>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div>
                  <div className="tw-overline mb-1.5 flex items-center justify-between">
                    <span>Admin response</span>
                    {!canManage && <span className="mono text-[10px] text-slate-400">read-only</span>}
                  </div>
                  {canManage ? (
                    <>
                      <textarea
                        className="input-sharp min-h-[110px]"
                        placeholder="Reply to the submitters — what's the plan? What's blocking? When can it ship?"
                        value={responseDraft}
                        onChange={(e) => setResponseDraft(e.target.value)}
                        data-testid="suggestion-view-response"
                      />
                      <div className="flex justify-end mt-2">
                        <button
                          type="button"
                          onClick={saveResponse}
                          disabled={responseBusy || responseDraft === (viewing.admin_note || "")}
                          className="btn-brand !py-2 !px-4 text-sm disabled:opacity-40"
                          data-testid="suggestion-view-save-response"
                        >
                          {responseBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Save response
                        </button>
                      </div>
                    </>
                  ) : (
                    viewing.admin_note
                      ? <p className="text-sm text-slate-700 whitespace-pre-wrap" data-testid="suggestion-view-response-readonly">{viewing.admin_note}</p>
                      : <p className="text-xs text-slate-400">No admin response yet.</p>
                  )}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}



/* ---------------- shared ---------------- */
function SectionShell({ title, onAdd, addTestId, extra, children }) {
  return (
    <div className="mt-6 bg-white border border-slate-200">
      <div className="flex items-center justify-between p-5 border-b border-slate-200 gap-3 flex-wrap">
        <h2 className="text-xl font-bold text-[#0B132B]">{title}</h2>
        <div className="flex items-center gap-2">
          {extra}
          <button className="btn-brand !py-2.5 !px-4 text-sm" onClick={onAdd} data-testid={addTestId}>
            <Plus className="h-4 w-4" /> New
          </button>
        </div>
      </div>
      <div className="p-2">{children}</div>
    </div>
  );
}

function CsvSampleDownloadButton({ filename, headers, sampleRow }) {
  const onDownload = () => {
    const headerLine = headers;
    const rows = sampleRow ? [headerLine, sampleRow] : [headerLine];
    const blob = new Blob([rows.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = filename; document.body.appendChild(a); a.click();
    document.body.removeChild(a); URL.revokeObjectURL(url);
  };
  return (
    <button
      className="btn-outline-navy !py-2.5 !px-3 text-sm"
      onClick={onDownload}
      title="Download sample CSV"
      data-testid={`csv-sample-${filename.replace(/\..*$/, "")}`}
    >
      <FileSpreadsheet className="h-4 w-4" /> Sample
    </button>
  );
}

function CsvImportButton({ kind, onDone, sampleHeaders }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const parseCsv = (text) => {
    const lines = text.split(/\r?\n/).filter((l) => l.trim().length > 0);
    if (lines.length === 0) return [];
    const headers = splitCsvLine(lines[0]).map((h) => h.trim());
    return lines.slice(1).map((line) => {
      const cells = splitCsvLine(line);
      const o = {};
      headers.forEach((h, i) => (o[h] = cells[i] !== undefined ? cells[i] : ""));
      return o;
    });
  };

  const coerce = (rows) => rows.map((r) => {
    const o = { ...r };
    Object.keys(o).forEach((k) => {
      const v = o[k];
      if (v === "true" || v === "TRUE") o[k] = true;
      else if (v === "false" || v === "FALSE") o[k] = false;
      else if (v !== "" && !isNaN(v) && typeof v === "string") o[k] = parseFloat(v);
    });
    if ("features" in o && typeof o.features === "string") {
      o.features = o.features ? o.features.split("|").map((s) => s.trim()).filter(Boolean) : [];
    }
    return o;
  });

  const onFile = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setBusy(true);
    try {
      const text = await f.text();
      const rows = coerce(parseCsv(text));
      if (!rows.length) { toast.error("Empty CSV"); return; }
      const { data } = await api.post(`/${kind}/bulk`, { items: rows });
      toast.success(`Imported ${data.inserted} ${kind}`);
      onDone();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || `Import failed: ${err.message}`);
    } finally { setBusy(false); if (inputRef.current) inputRef.current.value = ""; }
  };

  return (
    <>
      <input ref={inputRef} type="file" accept=".csv,text/csv" className="hidden" onChange={onFile} data-testid={`csv-input-${kind}`} />
      <button
        className="btn-outline-navy !py-2.5 !px-3 text-sm"
        onClick={() => inputRef.current?.click()}
        disabled={busy}
        title={`CSV headers: ${sampleHeaders}`}
        data-testid={`csv-import-${kind}`}
      >
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} CSV
      </button>
    </>
  );
}

function splitCsvLine(line) {
  const out = []; let cur = ""; let inQ = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (inQ) {
      if (ch === '"' && line[i + 1] === '"') { cur += '"'; i++; }
      else if (ch === '"') inQ = false;
      else cur += ch;
    } else {
      if (ch === '"') inQ = true;
      else if (ch === ",") { out.push(cur); cur = ""; }
      else cur += ch;
    }
  }
  out.push(cur);
  return out;
}

// ─────────────────────────────────────────────────────────────────────────────
// PROMO CSV — flat schema (one row per promo). Nested phone_pricing / tablet_pricing
// / home_internet_pricing / hotspot_pricing are built from flat columns.
//
// Required columns: title, device_id
// Optional general columns: description, starts_at, ends_at, active
// Phone — finance:           finance_enabled, finance_t1, finance_t2, finance_t3, finance_months, finance_fcot
// Phone — switch/join/no_hassle: <group>_<sub>_enabled, <group>_<sub>_price, <group>_<sub>_months, <group>_<sub>_devices, <group>_<sub>_port, <group>_<sub>_verify
//   where group ∈ switch|join|no_hassle and sub ∈ single|multi_month|multi_line
// Phone — full retail:       full_retail_enabled, full_retail_price, full_retail_port, full_retail_verify
// Tablet:                    tablet_standalone_enabled, tablet_standalone_price, tablet_bundle_enabled, tablet_bundle_price, tablet_bundle_months
// Home Internet:             hi_standalone_enabled, hi_standalone_price, hi_bundle_enabled, hi_bundle_price
// Hotspot:                   hotspot_enabled, hotspot_price
const PROMO_CSV_HEADERS = [
  "title", "description", "device_id", "starts_at", "ends_at", "active",
  "finance_enabled", "finance_t1", "finance_t2", "finance_t3", "finance_months", "finance_fcot",
  "switch_single_enabled", "switch_single_price", "switch_single_port", "switch_single_verify",
  "switch_multi_month_enabled", "switch_multi_month_price", "switch_multi_month_months", "switch_multi_month_port", "switch_multi_month_verify",
  "switch_multi_line_enabled", "switch_multi_line_price", "switch_multi_line_devices", "switch_multi_line_port", "switch_multi_line_verify",
  "join_single_enabled", "join_single_price", "join_single_verify",
  "join_multi_month_enabled", "join_multi_month_price", "join_multi_month_months", "join_multi_month_verify",
  "join_multi_line_enabled", "join_multi_line_price", "join_multi_line_devices", "join_multi_line_verify",
  "no_hassle_single_enabled", "no_hassle_single_price",
  "no_hassle_multi_month_enabled", "no_hassle_multi_month_price", "no_hassle_multi_month_months",
  "no_hassle_multi_line_enabled", "no_hassle_multi_line_price", "no_hassle_multi_line_devices",
  "full_retail_enabled", "full_retail_price", "full_retail_port", "full_retail_verify",
  "tablet_standalone_enabled", "tablet_standalone_price",
  "tablet_bundle_enabled", "tablet_bundle_price", "tablet_bundle_months",
  "hi_standalone_enabled", "hi_standalone_price",
  "hi_bundle_enabled", "hi_bundle_price",
  "hotspot_enabled", "hotspot_price",
].join(",");

function PROMO_CSV_SAMPLE_ROW() {
  // One example row demonstrating an iPhone 14-style promo (Switch & Join, no finance).
  return [
    "iPhone 14 Promo",                                       // title
    '"Switch, Join, or No Hassle on a 6.1in iPhone 14"',     // description (quoted because of commas)
    "REPLACE_WITH_DEVICE_ID",                                // device_id
    "2026-04-23",                                            // starts_at (YYYY-MM-DD)
    "2026-07-08",                                            // ends_at
    "true",                                                  // active
    "false", "0", "0", "0", "24", "0",                       // finance
    "true", "49.99", "required", "required",                 // switch.single
    "false", "0", "0", "optional", "optional",               // switch.multi_month
    "false", "0", "2", "optional", "optional",               // switch.multi_line
    "false", "0", "required",                                // join.single
    "true", "149.99", "3", "required",                       // join.multi_month
    "false", "0", "2", "required",                           // join.multi_line
    "true", "599.99",                                        // no_hassle.single
    "false", "0", "3",                                       // no_hassle.multi_month
    "false", "0", "2",                                       // no_hassle.multi_line
    "true", "599.99", "optional", "optional",                // full_retail
    "false", "0",                                            // tablet standalone
    "false", "0", "0",                                       // tablet bundle
    "false", "0",                                            // hi standalone
    "false", "0",                                            // hi bundle
    "false", "0",                                            // hotspot
  ].join(",");
}

function asBool(v) {
  if (typeof v === "boolean") return v;
  if (v == null) return false;
  const s = String(v).trim().toLowerCase();
  return s === "true" || s === "1" || s === "yes";
}
function asNum(v) {
  if (v === "" || v == null) return 0;
  const n = parseFloat(v);
  return Number.isNaN(n) ? 0 : n;
}
function asInt(v) {
  if (v === "" || v == null) return 0;
  const n = parseInt(v, 10);
  return Number.isNaN(n) ? 0 : n;
}
function asReqStr(v) {
  if (!v) return "not_required";
  const s = String(v).trim().toLowerCase();
  return s === "required" || s === "optional" ? s : "not_required";
}
function asIsoOrNull(v) {
  if (!v) return null;
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

/** Convert a single flat CSV row into a nested PromoIn payload. */
function flatRowToPromo(r) {
  // Phone pricing — only emit when any phone field is enabled.
  const phoneAny = [
    "finance_enabled",
    "switch_single_enabled", "switch_multi_month_enabled", "switch_multi_line_enabled",
    "join_single_enabled", "join_multi_month_enabled", "join_multi_line_enabled",
    "no_hassle_single_enabled", "no_hassle_multi_month_enabled", "no_hassle_multi_line_enabled",
    "full_retail_enabled",
  ].some((k) => asBool(r[k]));
  const phone_pricing = phoneAny ? {
    finance: {
      enabled: asBool(r.finance_enabled),
      device_price_tier1: asNum(r.finance_t1),
      device_price_tier2: asNum(r.finance_t2),
      device_price_tier3: asNum(r.finance_t3),
      months: asInt(r.finance_months) || 24,
      fcot_credit: asNum(r.finance_fcot),
      eligible_plan_ids: [],
    },
    switch: {
      single_month: { enabled: asBool(r.switch_single_enabled),       device_price: asNum(r.switch_single_price),       required_months: 0,                              required_devices: 0,                                port_in: asReqStr(r.switch_single_port),       verify_id: asReqStr(r.switch_single_verify),       eligible_plan_ids: [], eligible_carrier_ids: [] },
      multi_month:  { enabled: asBool(r.switch_multi_month_enabled),  device_price: asNum(r.switch_multi_month_price),  required_months: asInt(r.switch_multi_month_months), required_devices: 0,                            port_in: asReqStr(r.switch_multi_month_port),  verify_id: asReqStr(r.switch_multi_month_verify),  eligible_plan_ids: [], eligible_carrier_ids: [] },
      multi_line:   { enabled: asBool(r.switch_multi_line_enabled),   device_price: asNum(r.switch_multi_line_price),   required_months: 0,                              required_devices: asInt(r.switch_multi_line_devices) || 2, port_in: asReqStr(r.switch_multi_line_port),   verify_id: asReqStr(r.switch_multi_line_verify),   eligible_plan_ids: [], eligible_carrier_ids: [] },
    },
    join: {
      single_month: { enabled: asBool(r.join_single_enabled),         device_price: asNum(r.join_single_price),         required_months: 0,                              required_devices: 0,                                port_in: "not_required",                       verify_id: asReqStr(r.join_single_verify),         eligible_plan_ids: [], eligible_carrier_ids: [] },
      multi_month:  { enabled: asBool(r.join_multi_month_enabled),    device_price: asNum(r.join_multi_month_price),    required_months: asInt(r.join_multi_month_months),   required_devices: 0,                            port_in: "not_required",                       verify_id: asReqStr(r.join_multi_month_verify),    eligible_plan_ids: [], eligible_carrier_ids: [] },
      multi_line:   { enabled: asBool(r.join_multi_line_enabled),     device_price: asNum(r.join_multi_line_price),     required_months: 0,                              required_devices: asInt(r.join_multi_line_devices) || 2,   port_in: "not_required",                       verify_id: asReqStr(r.join_multi_line_verify),     eligible_plan_ids: [], eligible_carrier_ids: [] },
    },
    no_hassle: {
      single_month: { enabled: asBool(r.no_hassle_single_enabled),       device_price: asNum(r.no_hassle_single_price),       required_months: 0,                                  required_devices: 0,                                    port_in: "not_required", verify_id: "not_required", eligible_plan_ids: [], eligible_carrier_ids: [] },
      multi_month:  { enabled: asBool(r.no_hassle_multi_month_enabled),  device_price: asNum(r.no_hassle_multi_month_price),  required_months: asInt(r.no_hassle_multi_month_months),    required_devices: 0,                                port_in: "not_required", verify_id: "not_required", eligible_plan_ids: [], eligible_carrier_ids: [] },
      multi_line:   { enabled: asBool(r.no_hassle_multi_line_enabled),   device_price: asNum(r.no_hassle_multi_line_price),   required_months: 0,                                  required_devices: asInt(r.no_hassle_multi_line_devices) || 2, port_in: "not_required", verify_id: "not_required", eligible_plan_ids: [], eligible_carrier_ids: [] },
    },
    full_retail: {
      enabled: asBool(r.full_retail_enabled),
      price: asNum(r.full_retail_price),
      verify_id: asReqStr(r.full_retail_verify),
      port_in: asReqStr(r.full_retail_port),
      eligible_carrier_ids: [],
      eligible_plan_ids: [],
    },
  } : null;

  // Tablet / HI / Hotspot pricing — only emit when at least one variant is enabled.
  const tabletAny = asBool(r.tablet_standalone_enabled) || asBool(r.tablet_bundle_enabled);
  const tablet_pricing = tabletAny ? {
    standalone: { enabled: asBool(r.tablet_standalone_enabled), price: asNum(r.tablet_standalone_price), eligible_plan_ids: [] },
    bundle:     { enabled: asBool(r.tablet_bundle_enabled),     price: asNum(r.tablet_bundle_price),     eligible_plan_ids: [], required_months: asInt(r.tablet_bundle_months) },
  } : null;
  const hiAny = asBool(r.hi_standalone_enabled) || asBool(r.hi_bundle_enabled);
  const home_internet_pricing = hiAny ? {
    standalone: { enabled: asBool(r.hi_standalone_enabled), price: asNum(r.hi_standalone_price) },
    bundle:     { enabled: asBool(r.hi_bundle_enabled),     price: asNum(r.hi_bundle_price) },
  } : null;
  const hotspot_pricing = asBool(r.hotspot_enabled) ? { enabled: true, price: asNum(r.hotspot_price), eligible_plan_ids: [] } : null;

  return {
    title: r.title || "",
    description: r.description || "",
    device_id: r.device_id || null,
    starts_at: asIsoOrNull(r.starts_at),
    ends_at: asIsoOrNull(r.ends_at),
    active: asBool(r.active),
    phone_pricing,
    tablet_pricing,
    home_internet_pricing,
    hotspot_pricing,
  };
}

function PromoCsvImportButton({ onDone }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const onFile = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setBusy(true);
    try {
      const text = await f.text();
      const lines = text.split(/\r?\n/).filter((l) => l.trim().length > 0);
      if (lines.length < 2) { toast.error("CSV has no data rows"); return; }
      const headers = splitCsvLine(lines[0]).map((h) => h.trim());
      const rows = lines.slice(1).map((line) => {
        const cells = splitCsvLine(line);
        const o = {};
        headers.forEach((h, i) => (o[h] = cells[i] !== undefined ? cells[i] : ""));
        return o;
      });
      const promos = rows.map(flatRowToPromo);
      const { data } = await api.post("/promos/bulk", { items: promos });
      toast.success(`Imported ${data.inserted} promo${data.inserted === 1 ? "" : "s"}`);
      onDone();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || `Import failed: ${err.message}`);
    } finally { setBusy(false); if (inputRef.current) inputRef.current.value = ""; }
  };
  return (
    <>
      <input ref={inputRef} type="file" accept=".csv,text/csv" className="hidden" onChange={onFile} data-testid="csv-input-promos" />
      <button
        className="btn-outline-navy !py-2.5 !px-3 text-sm"
        onClick={() => inputRef.current?.click()}
        disabled={busy}
        data-testid="csv-import-promos"
      >
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} CSV
      </button>
    </>
  );
}


function Table({ headers, children }) {
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="text-left border-b border-slate-200">
          {headers.map((h, i) => <th key={i} className="tw-overline py-3 px-4">{h}</th>)}
        </tr>
      </thead>
      <tbody>{children}</tbody>
    </table>
  );
}
function Td({ children, className = "" }) { return <td className={`py-4 px-4 align-middle ${className}`}>{children}</td>; }
function Active({ on }) { return <span className={`badge-pill ${on ? "badge-teal" : "badge-outline"}`}>{on ? "Active" : "Inactive"}</span>; }
function Empty({ label }) { return <div className="py-16 text-center text-slate-500" data-testid="admin-empty">{label}</div>; }
function Loading() { return <div className="py-16 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-slate-400" /></div>; }
function Field({ label, children, full, info }) {
  return (
    <label className={`flex flex-col gap-1.5 min-w-0 ${full ? "sm:col-span-2" : ""}`}>
      <span className="tw-overline flex items-center gap-1.5 min-w-0">
        <span className="truncate" title={label}>{label}</span>
        {info && (
          <span
            title={info}
            aria-label={info}
            className="inline-flex items-center justify-center h-4 w-4 text-slate-400 hover:text-[#0B132B] cursor-help shrink-0"
            data-testid="field-info-icon"
          >
            <Info className="h-3.5 w-3.5" />
          </span>
        )}
      </span>
      {children}
    </label>
  );
}
function Toggle({ label, v, onChange, testKey }) {
  return (
    <label className="flex items-center justify-between border border-slate-200 px-3 py-2 cursor-pointer hover:border-slate-400">
      <span className="text-sm font-medium text-[#0B132B]">{label}</span>
      <button type="button" onClick={() => onChange(!v)} className={`h-6 w-11 ${v ? "bg-[#00E5FF]" : "bg-slate-300"} relative transition`} data-testid={testKey || undefined}>
        <span className={`absolute top-0.5 ${v ? "left-6" : "left-0.5"} h-5 w-5 bg-[#0B132B] transition-all`} />
      </button>
    </label>
  );
}
function RowActions({ onEdit, onDuplicate, onDelete, testKey, canEdit = true, canDelete = true }) {
  return (
    <div className="inline-flex gap-2">
      {canEdit && (
        <button onClick={onEdit} className="p-2 hover:bg-slate-100" data-testid={`row-edit-${testKey}`}><Pencil className="h-4 w-4 text-slate-600" /></button>
      )}
      {onDuplicate && canEdit && (
        <button onClick={onDuplicate} className="p-2 hover:bg-[#00E5FF]/20" title="Duplicate" data-testid={`row-duplicate-${testKey}`}>
          <Copy className="h-4 w-4 text-[#008B99]" />
        </button>
      )}
      {canDelete && (
        <button onClick={onDelete} className="p-2 hover:bg-red-50" data-testid={`row-delete-${testKey}`}><Trash2 className="h-4 w-4 text-red-600" /></button>
      )}
    </div>
  );
}

function PlanTiersEditor({ tiers, onChange }) {
  const upd = (i, patch) => {
    const next = tiers.map((t, idx) => (idx === i ? { ...t, ...patch } : t));
    onChange(next);
  };
  const add = () => onChange([...tiers, { from_line: nextStart(tiers), to_line: nextStart(tiers), price: 0 }]);
  const remove = (i) => onChange(tiers.filter((_, idx) => idx !== i));

  return (
    <div className="sm:col-span-2 border border-slate-200 p-4" data-testid="plan-tiers-editor">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="tw-overline">Per-line price tiers</div>
          <div className="text-xs text-slate-500 mt-1">
            Optional. Overrides the multi-line discount %. Define a price for each line number (e.g. line 1 = $65, line 2 = $30, lines 5-10 = $30).
          </div>
        </div>
        <button type="button" onClick={add} className="btn-outline-navy !py-2 !px-3 text-xs" data-testid="plan-tier-add">
          <Plus className="h-3.5 w-3.5" /> Add tier
        </button>
      </div>
      {tiers.length === 0 ? (
        <div className="text-xs text-slate-400 py-3 text-center border border-dashed border-slate-200">
          No tiers — using legacy mode (price for line 1 + % discount for extra lines).
        </div>
      ) : (
        <div className="space-y-2">
          {tiers.map((tier, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`plan-tier-row-${i}`}>
              <label className="col-span-3">
                <div className="tw-overline text-[10px]">From line</div>
                <input type="number" min="1" className="input-sharp !py-2 mt-1" value={tier.from_line}
                  onChange={(e) => upd(i, { from_line: parseInt(e.target.value || 1, 10) })}
                  data-testid={`plan-tier-from-${i}`} />
              </label>
              <label className="col-span-3">
                <div className="tw-overline text-[10px]">To line</div>
                <input type="number" min="1" className="input-sharp !py-2 mt-1" value={tier.to_line}
                  onChange={(e) => upd(i, { to_line: parseInt(e.target.value || 1, 10) })}
                  data-testid={`plan-tier-to-${i}`} />
              </label>
              <label className="col-span-5">
                <div className="tw-overline text-[10px]">Price / line ($)</div>
                <input type="number" step="0.01" min="0" className="input-sharp !py-2 mt-1" value={tier.price}
                  onChange={(e) => upd(i, { price: parseFloat(e.target.value || 0) })}
                  data-testid={`plan-tier-price-${i}`} />
              </label>
              <button type="button" onClick={() => remove(i)} className="col-span-1 mt-5 p-2 hover:bg-red-50 grid place-items-center" data-testid={`plan-tier-remove-${i}`}>
                <Trash2 className="h-4 w-4 text-red-600" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function nextStart(tiers) {
  if (!tiers.length) return 1;
  const maxTo = Math.max(...tiers.map((t) => t.to_line || 0));
  return maxTo + 1;
}
